import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { fleetOpsApi as A } from "../api/fleetOpsApi";
import {
  parseVehicleScreenshotInBrowser,
  parseVehicleScreenshotsInBrowser,
} from "../utils/browserScreenshotOcr";
import s from "./FleetOps.module.css";
import FleetScopeUserPicker from "../components/FleetScopeUserPicker";

const TRANSPORDIAMET_URL =
  "https://eteenindus.mnt.ee/public/soidukTaustakontroll.jsf";

const statusClass = (value) => s[value] || s.unknown;
const normalizeIdentifier = (value) =>
  String(value || "")
    .toUpperCase()
    .replace(/\s+/g, "");
const normalizePlate = (value) =>
  String(value || "")
    .toUpperCase()
    .replace(/[^A-Z0-9ÄÖÜÕ-]/g, "")
    .slice(0, 16);
const normalizeVin = (value) =>
  String(value || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 17);
const normalizeIsoDate = (value) => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`;
  }

  const raw = String(value || "").trim();
  if (!raw) return "";

  // Native <input type="date"> value and ISO timestamps.
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;

  // Full dates produced by OCR/browser locale. 12/31/2026 is US locale,
  // 31.12.2026 is the common Estonian/Russian representation.
  const full = raw.match(/^(\d{1,2})[\/.\-](\d{1,2})[\/.\-](\d{4})$/);
  if (full) {
    let a = Number(full[1]);
    let b = Number(full[2]);
    const year = Number(full[3]);
    let month;
    let day;

    if (a > 12 && b <= 12) {
      day = a; month = b;
    } else if (b > 12 && a <= 12) {
      month = a; day = b;
    } else {
      // Ambiguous values are treated as month/day because browser date
      // controls on Windows often render that way. OCR month.year is handled below.
      month = a; day = b;
    }

    const d = new Date(year, month - 1, day);
    if (
      d.getFullYear() === year &&
      d.getMonth() === month - 1 &&
      d.getDate() === day
    ) {
      return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    }
  }

  // Transpordiamet often displays only month.year (e.g. 12.2026).
  const monthYear = raw.match(/^(0?[1-9]|1[0-2])[\/.\-\s]+(\d{4})$/);
  if (monthYear) {
    const month = Number(monthYear[1]);
    const year = Number(monthYear[2]);
    const lastDay = new Date(year, month, 0).getDate();
    return `${year}-${String(month).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
  }

  return raw;
};
const isVin = (value) => /^[A-HJ-NPR-Z0-9]{17}$/.test(value);
const isPlate = (value) => /^[A-Z0-9ÄÖÜÕ-]{3,16}$/.test(value);
const isIsoDate = (value) => {
  const normalized = normalizeIsoDate(value);
  const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
};

const FLEET_MODAL_FALLBACKS = {
  ru: {
    fleet_batch_title: "Загрузить транспорт из PDF или скриншотов",
    fleet_batch_hint: "Перетащите PDF или скриншоты Transpordiamet. PDF читается напрямую из текста и является наиболее точным вариантом импорта.",
    fleet_batch_drop_title: "Перетащите PDF или скриншоты сюда",
    fleet_batch_drop_subtitle: "Или нажмите на область и выберите сразу несколько PDF, PNG или JPG файлов. Форматы можно смешивать.",
    fleet_batch_limit: "до 50 файлов",
    fleet_batch_processing: "Читаем данные транспорта…",
    fleet_batch_images_only: "Поддерживаются только PDF, PNG и JPG файлы.",
    fleet_batch_queue_title: "Файлов в списке: {{count}}",
    fleet_batch_ready_summary: "Готово к сохранению: {{ready}} · Нужно проверить: {{review}}",
    fleet_batch_clear: "Очистить список",
    fleet_batch_status_queued: "В очереди",
    fleet_batch_status_processing: "Распознаётся",
    fleet_batch_status_ready: "Готово",
    fleet_batch_status_review: "Проверить",
    fleet_batch_status_error: "Ошибка чтения",
    fleet_batch_status_saving: "Сохраняется",
    fleet_batch_status_saved: "Сохранено",
    fleet_batch_status_save_error: "Ошибка сохранения",
    fleet_batch_review_hint: "Проверьте обязательные данные перед сохранением.",
    fleet_batch_missing_registration: "Не найден регистрационный номер.",
    fleet_batch_missing_deadline: "Не найден корректный срок действия техосмотра.",
    fleet_batch_save_title: "Сохранить распознанный транспорт",
    fleet_batch_save_hint: "В базу попадут только строки с корректным номером и сроком техосмотра.",
    fleet_batch_save_button: "Сохранить: {{count}}",
    fleet_batch_saved_count: "Сохранено машин: {{count}}",
    fleet_batch_stage_upload: "Загрузка на сервер",
    fleet_batch_stage_parse: "Распознавание",
    fleet_batch_stage_save: "Сохранение",
    fleet_batch_stage_done: "Готово",
    fleet_batch_progress_title: "Обрабатываем файлы",
    fleet_batch_progress_file: "Сейчас: {{name}}",
    fleet_batch_progress_summary: "{{done}} из {{total}} файлов",
    fleet_batch_failed_title: "Не обработались: {{count}}",
    fleet_batch_failed_hint: "Файлы сохранены на сервере. Можно повторить обработку или скачать отчёт и передать его для адаптации parser-а.",
    fleet_batch_retry_failed: "Попробовать ещё раз",
    fleet_batch_download_report: "Скачать отчёт ошибок",
    fleet_batch_source_saved: "Исходный PDF сохранён",
    fleet_batch_retrying: "Повторная обработка…",
    fleet_batch_error_unknown: "Неизвестная ошибка обработки файла",
    fleet_batch_all_processed: "Все файлы обработаны. Проверьте данные и сохраните транспорт.",
    fleet_batch_save_progress_title: "Сохраняем транспорт",
    fleet_batch_save_progress_summary: "Сохранено {{done}} из {{total}}",
    fleet_batch_close_after_errors: "Закрыть и оставить список",
    fleet_batch_report_created: "Отчёт об ошибках создан.",
    fleet_or_add_manually: "Или добавить одну машину вручную",
    fleet_identity_hint_compact: "Введите номер и VIN вручную, если не используете скриншот.",
    fleet_ocr_field_detected: "Распознано",
    fleet_ocr_field_missing: "Не распознано",
    fleet_registration_enter_placeholder: "Введите регистрационный номер",
    fleet_vin_enter_placeholder: "Введите 17-значный VIN",
    fleet_manual_save_button: "Сохранить транспорт",
  },
  en: {
    fleet_batch_title: "Import vehicles from PDF or screenshots",
    fleet_batch_hint: "Drop Transpordiamet PDFs or screenshots here. PDFs are read directly from their text layer and are the most reliable import format.",
    fleet_batch_drop_title: "Drop PDFs or screenshots here",
    fleet_batch_drop_subtitle: "Or click this area and select multiple PDF, PNG or JPG files at once. Formats can be mixed.",
    fleet_batch_limit: "up to 50 files",
    fleet_batch_processing: "Reading vehicle data…",
    fleet_batch_images_only: "Only PDF, PNG and JPG files are supported.",
    fleet_batch_queue_title: "Files in queue: {{count}}",
    fleet_batch_ready_summary: "Ready to save: {{ready}} · Needs review: {{review}}",
    fleet_batch_clear: "Clear list",
    fleet_batch_status_queued: "Queued",
    fleet_batch_status_processing: "Reading",
    fleet_batch_status_ready: "Ready",
    fleet_batch_status_review: "Review",
    fleet_batch_status_error: "Read error",
    fleet_batch_status_saving: "Saving",
    fleet_batch_status_saved: "Saved",
    fleet_batch_status_save_error: "Save error",
    fleet_batch_review_hint: "Check the required data before saving.",
    fleet_batch_missing_registration: "Registration number is missing.",
    fleet_batch_missing_deadline: "A valid inspection expiry date is missing.",
    fleet_batch_save_title: "Save detected vehicles",
    fleet_batch_save_hint: "Only rows with a valid registration number and inspection expiry are saved.",
    fleet_batch_save_button: "Save: {{count}}",
    fleet_batch_saved_count: "Vehicles saved: {{count}}",
    fleet_batch_stage_upload: "Upload to server",
    fleet_batch_stage_parse: "Recognition",
    fleet_batch_stage_save: "Save",
    fleet_batch_stage_done: "Done",
    fleet_batch_progress_title: "Processing files",
    fleet_batch_progress_file: "Current: {{name}}",
    fleet_batch_progress_summary: "{{done}} of {{total}} files",
    fleet_batch_failed_title: "Failed to process: {{count}}",
    fleet_batch_failed_hint: "The source files were preserved on the server. Retry them or download an error report for parser adaptation.",
    fleet_batch_retry_failed: "Retry failed",
    fleet_batch_download_report: "Download error report",
    fleet_batch_source_saved: "Source PDF saved",
    fleet_batch_retrying: "Retrying…",
    fleet_batch_error_unknown: "Unknown file processing error",
    fleet_batch_all_processed: "All files were processed. Review the detected data and save the vehicles.",
    fleet_batch_save_progress_title: "Saving vehicles",
    fleet_batch_save_progress_summary: "Saved {{done}} of {{total}}",
    fleet_batch_close_after_errors: "Close and keep list",
    fleet_batch_report_created: "Error report created.",
    fleet_or_add_manually: "Or add one vehicle manually",
    fleet_identity_hint_compact: "Enter the registration number and VIN manually if you are not using a screenshot.",
    fleet_ocr_field_detected: "Detected",
    fleet_ocr_field_missing: "Not detected",
    fleet_registration_enter_placeholder: "Enter registration number",
    fleet_vin_enter_placeholder: "Enter 17-character VIN",
    fleet_manual_save_button: "Save vehicle",
  },
  et: {
    fleet_batch_title: "Lisa sõidukid PDF-ist või ekraanipiltidelt",
    fleet_batch_hint: "Lohista siia Transpordiameti PDF-id või ekraanipildid. PDF loetakse otse tekstikihist ja on kõige täpsem impordiviis.",
    fleet_batch_drop_title: "Lohista PDF-id või ekraanipildid siia",
    fleet_batch_drop_subtitle: "Või klõpsa alal ja vali korraga mitu PDF-, PNG- või JPG-faili. Vorminguid võib segada.",
    fleet_batch_limit: "kuni 50 faili",
    fleet_batch_processing: "Sõidukiandmeid loetakse…",
    fleet_batch_images_only: "Toetatud on ainult PDF-, PNG- ja JPG-failid.",
    fleet_batch_queue_title: "Faile järjekorras: {{count}}",
    fleet_batch_ready_summary: "Salvestamiseks valmis: {{ready}} · Vajab kontrolli: {{review}}",
    fleet_batch_clear: "Tühjenda nimekiri",
    fleet_batch_status_queued: "Järjekorras",
    fleet_batch_status_processing: "Tuvastamine",
    fleet_batch_status_ready: "Valmis",
    fleet_batch_status_review: "Kontrolli",
    fleet_batch_status_error: "Lugemisviga",
    fleet_batch_status_saving: "Salvestamine",
    fleet_batch_status_saved: "Salvestatud",
    fleet_batch_status_save_error: "Salvestusviga",
    fleet_batch_review_hint: "Kontrolli enne salvestamist kohustuslikke andmeid.",
    fleet_batch_missing_registration: "Registreerimisnumber puudub.",
    fleet_batch_missing_deadline: "Kehtiv ülevaatuse lõppkuupäev puudub.",
    fleet_batch_save_title: "Salvesta tuvastatud sõidukid",
    fleet_batch_save_hint: "Salvestatakse ainult korrektse registreerimisnumbri ja ülevaatuse tähtajaga read.",
    fleet_batch_save_button: "Salvesta: {{count}}",
    fleet_batch_saved_count: "Sõidukeid salvestatud: {{count}}",
    fleet_batch_stage_upload: "Laadimine serverisse",
    fleet_batch_stage_parse: "Tuvastamine",
    fleet_batch_stage_save: "Salvestamine",
    fleet_batch_stage_done: "Valmis",
    fleet_batch_progress_title: "Failide töötlemine",
    fleet_batch_progress_file: "Praegu: {{name}}",
    fleet_batch_progress_summary: "{{done}} / {{total}} faili",
    fleet_batch_failed_title: "Töötlemata faile: {{count}}",
    fleet_batch_failed_hint: "Lähtefailid säilitati serveris. Proovi uuesti või laadi alla veaaruanne parseri kohandamiseks.",
    fleet_batch_retry_failed: "Proovi ebaõnnestunuid uuesti",
    fleet_batch_download_report: "Laadi veaaruanne alla",
    fleet_batch_source_saved: "Algne PDF salvestatud",
    fleet_batch_retrying: "Uuesti töötlemine…",
    fleet_batch_error_unknown: "Tundmatu failitöötluse viga",
    fleet_batch_all_processed: "Kõik failid on töödeldud. Kontrolli andmeid ja salvesta sõidukid.",
    fleet_batch_save_progress_title: "Sõidukite salvestamine",
    fleet_batch_save_progress_summary: "Salvestatud {{done}} / {{total}}",
    fleet_batch_close_after_errors: "Sulge ja jäta nimekiri alles",
    fleet_batch_report_created: "Veaaruanne on loodud.",
    fleet_or_add_manually: "Või lisa üks sõiduk käsitsi",
    fleet_identity_hint_compact: "Sisesta registreerimisnumber ja VIN käsitsi, kui ekraanipilti ei kasutata.",
    fleet_ocr_field_detected: "Tuvastatud",
    fleet_ocr_field_missing: "Ei tuvastatud",
    fleet_registration_enter_placeholder: "Sisestage registreerimisnumber",
    fleet_vin_enter_placeholder: "Sisestage 17-kohaline VIN",
    fleet_manual_save_button: "Salvesta sõiduk",
  },
};


const FLEET_TELEGRAM_FALLBACKS = {
  ru: {
    fleet_telegram_title: "Telegram-уведомления",
    fleet_telegram_subtitle: "Подключите Telegram-получателя и выберите, какие напоминания отправлять по автопарку.",
    fleet_telegram_checking: "Проверяем…",
    fleet_telegram_connected: "Подключено",
    fleet_telegram_not_connected: "Не подключено",
    fleet_telegram_schedule: "Время проверки",
    fleet_telegram_schedule_settings: "Настроить расписание",
    fleet_telegram_schedule_title: "Расписание Telegram-напоминаний",
    fleet_telegram_schedule_subtitle: "Часовой пояс и понятное расписание сигналов для каждого уровня риска.",
    fleet_telegram_schedule_save: "Сохранить расписание",
    fleet_telegram_schedule_cancel: "Отмена",
    fleet_telegram_schedule_general: "Основное",
    fleet_telegram_schedule_yellow: "Жёлтый режим",
    fleet_telegram_schedule_red: "Красный режим",
    fleet_telegram_schedule_green: "Зелёный → жёлтый",
    fleet_telegram_schedule_yellow_hint: "После первого сигнала: пока остаётся 8–23 дня — один сигнал в неделю.",
    fleet_telegram_schedule_red_hint: "7 дней и меньше, а также просрочка — сигналы каждый день; количество задаёте сами.",
    fleet_telegram_chat_label: "Чат, группа или канал Telegram",
    fleet_telegram_chat_placeholder: "Например: 123456789, -1001234567890 или @channel",
    fleet_telegram_chat_hint: "Для канала добавьте бота администратором и разрешите ему публиковать сообщения.",
    fleet_telegram_subscriptions: "Напоминания",
    fleet_telegram_inspection: "Техосмотр",
    fleet_telegram_insurance: "Страховка",
    fleet_telegram_save: "Сохранить",
    fleet_telegram_sending: "Отправляем…",
    fleet_telegram_test_inspection: "Проверить техосмотр",
    fleet_telegram_test_insurance: "Проверить страховку",
    fleet_telegram_saved: "Настройки Telegram сохранены.",
    fleet_telegram_load_error: "Не удалось загрузить настройки Telegram.",
    fleet_telegram_save_error: "Не удалось сохранить настройки Telegram.",
    fleet_telegram_test_error: "Не удалось отправить тестовое сообщение. Проверьте подключение бота и получателя.",
    fleet_telegram_test_inspection_ok: "Тестовое напоминание о техосмотре отправлено. Проверьте Telegram.",
    fleet_telegram_test_insurance_ok: "Тестовое напоминание о страховке отправлено. Проверьте Telegram.",
    fleet_telegram_bot_not_configured: "Telegram-бот не настроен. Укажите Bot Token в общих настройках Telegram CRM.",
    fleet_telegram_bot_disabled: "Telegram-бот настроен, но интеграция выключена. Включите Telegram в настройках CRM.",
    fleet_telegram_invalid_chat: "Укажите корректный Telegram Chat ID, ID группы/канала или @username канала.",
    fleet_telegram_disabled_error: "Telegram выключен в настройках CRM.",
    fleet_telegram_token_missing_error: "Bot Token Telegram не настроен.",
    fleet_telegram_recipient_missing_error: "Получатель Telegram для FleetOps не указан.",
    fleet_telegram_timezone: "Часовой пояс",
    fleet_telegram_green_time: "Сигнал при 30 днях",
    fleet_telegram_yellow_time: "Еженедельный сигнал — время",
    fleet_telegram_red_start: "Красный — начать",
    fleet_telegram_red_end: "Красный — закончить",
    fleet_telegram_red_count: "Красных сигналов в день",
    fleet_telegram_rules_title: "Порядок напоминаний",
    fleet_telegram_schedule_green_hint: "Больше 30 дней — тишина. При переходе на 30 дней приходит первый сигнал.",
    fleet_telegram_timezone_search_hint: "Выберите часовой пояс из списка или найдите его по городу, региону или UTC-смещению.",
    fleet_telegram_timezone_search_placeholder: "Поиск часового пояса…",
    fleet_telegram_timezone_no_results: "Часовой пояс не найден.",
    fleet_telegram_timezone_selected_now: "Сейчас в выбранной зоне",
    fleet_telegram_timezone_local_now: "Ваше локальное время",
    fleet_telegram_time_pair: "По зоне: {{zone}} · у вас: {{local}}",
    fleet_telegram_local_time_short: "У вас: {{time}}",
    fleet_telegram_green_rule: "Больше 30 дней — тишина; при 30 днях первый сигнал в {{time}}.",
    fleet_telegram_yellow_rule: "После первого сигнала и до 8 дней — один сигнал в неделю в {{time}}.",
    fleet_telegram_red_rule: "7 дней или меньше / просрочено — каждый день {{count}} раз(а) с {{start}} до {{end}}, равномерно.",
    fleet_telegram_interval: "Интервал: примерно каждые {{interval}}.",
    fleet_telegram_interval_once: "Один сигнал в {{time}}.",
    fleet_telegram_until_action: "Сигналы продолжаются, пока не нажато «Отключить на сегодня» или «Техосмотр сделан». После «Отключить на сегодня» уведомления автоматически возобновятся завтра.",
    fleet_telegram_personal_connected: "Личный Telegram подключён. Уведомления приходят только по вашим машинам.",
    fleet_telegram_personal_missing: "Личный Telegram не подключён. Подключите его в «Мой аккаунт», чтобы получать уведомления по своим машинам.",
    fleet_telegram_global_recipient: "Общий получатель FleetOps для суперадмина и машин без владельца.",
    fleet_telegram_account_link: "Открыть «Мой аккаунт»",

  },
  en: {
    fleet_telegram_title: "Telegram notifications",
    fleet_telegram_subtitle: "Connect a Telegram recipient and choose which fleet reminders should be sent.",
    fleet_telegram_checking: "Checking…",
    fleet_telegram_connected: "Connected",
    fleet_telegram_not_connected: "Not connected",
    fleet_telegram_schedule: "Check time",
    fleet_telegram_schedule_settings: "Configure schedule",
    fleet_telegram_schedule_title: "Telegram reminder schedule",
    fleet_telegram_schedule_subtitle: "Timezone and a clear reminder schedule for every risk level.",
    fleet_telegram_schedule_save: "Save schedule",
    fleet_telegram_schedule_cancel: "Cancel",
    fleet_telegram_schedule_general: "General",
    fleet_telegram_schedule_yellow: "Yellow mode",
    fleet_telegram_schedule_red: "Red mode",
    fleet_telegram_schedule_green: "Green → yellow",
    fleet_telegram_schedule_yellow_hint: "After the first alert: while 8–23 days remain — one reminder per week.",
    fleet_telegram_schedule_red_hint: "7 days or less, including overdue — daily reminders; you choose how many.",
    fleet_telegram_chat_label: "Telegram chat, group or channel",
    fleet_telegram_chat_placeholder: "For example: 123456789, -1001234567890 or @channel",
    fleet_telegram_chat_hint: "For a channel, add the bot as an administrator and allow it to publish messages.",
    fleet_telegram_subscriptions: "Reminders",
    fleet_telegram_inspection: "Technical inspection",
    fleet_telegram_insurance: "Insurance",
    fleet_telegram_save: "Save",
    fleet_telegram_sending: "Sending…",
    fleet_telegram_test_inspection: "Test inspection",
    fleet_telegram_test_insurance: "Test insurance",
    fleet_telegram_saved: "Telegram settings saved.",
    fleet_telegram_load_error: "Could not load Telegram settings.",
    fleet_telegram_save_error: "Could not save Telegram settings.",
    fleet_telegram_test_error: "Could not send the test message. Check the bot and recipient configuration.",
    fleet_telegram_test_inspection_ok: "Inspection test reminder sent. Check Telegram.",
    fleet_telegram_test_insurance_ok: "Insurance test reminder sent. Check Telegram.",
    fleet_telegram_bot_not_configured: "Telegram bot is not configured. Add the Bot Token in the shared CRM Telegram settings.",
    fleet_telegram_bot_disabled: "Telegram bot is configured, but the integration is disabled. Enable Telegram in CRM settings.",
    fleet_telegram_invalid_chat: "Enter a valid Telegram Chat ID, group/channel ID or @channel username.",
    fleet_telegram_disabled_error: "Telegram is disabled in CRM settings.",
    fleet_telegram_token_missing_error: "Telegram Bot Token is not configured.",
    fleet_telegram_recipient_missing_error: "FleetOps Telegram recipient is not configured.",
    fleet_telegram_timezone: "Timezone",
    fleet_telegram_green_time: "30-day alert time",
    fleet_telegram_yellow_time: "Weekly alert time",
    fleet_telegram_red_start: "Red alerts start",
    fleet_telegram_red_end: "Red alerts end",
    fleet_telegram_red_count: "Red alerts per day",
    fleet_telegram_rules_title: "Reminder order",
    fleet_telegram_schedule_green_hint: "More than 30 days — quiet. The first alert is sent when 30 days remain.",
    fleet_telegram_timezone_search_hint: "Choose a timezone from the list or find it by city, region, or UTC offset.",
    fleet_telegram_timezone_search_placeholder: "Search timezone…",
    fleet_telegram_timezone_no_results: "No timezone found.",
    fleet_telegram_timezone_selected_now: "Current time in selected zone",
    fleet_telegram_timezone_local_now: "Your local time",
    fleet_telegram_time_pair: "Zone: {{zone}} · yours: {{local}}",
    fleet_telegram_local_time_short: "Yours: {{time}}",
    fleet_telegram_green_rule: "More than 30 days — quiet; at 30 days the first alert is sent at {{time}}.",
    fleet_telegram_yellow_rule: "After the first alert and until 8 days remain — one reminder per week at {{time}}.",
    fleet_telegram_red_rule: "7 days or less / overdue — every day {{count}} time(s) from {{start}} to {{end}}, evenly spaced.",
    fleet_telegram_interval: "Interval: approximately every {{interval}}.",
    fleet_telegram_interval_once: "One reminder at {{time}}.",
    fleet_telegram_until_action: "Reminders continue until “Mute for today” or “Inspection completed” is pressed. A one-day mute resumes automatically tomorrow.",
    fleet_telegram_personal_connected: "Personal Telegram is linked. You receive reminders only for your vehicles.",
    fleet_telegram_personal_missing: "Personal Telegram is not linked. Link it in My Account to receive reminders for your vehicles.",
    fleet_telegram_global_recipient: "Shared FleetOps recipient for the superadmin and unowned vehicles.",
    fleet_telegram_account_link: "Open My Account",

  },
  et: {
    fleet_telegram_title: "Telegrami teavitused",
    fleet_telegram_subtitle: "Ühenda Telegrami saaja ja vali, milliseid sõidukipargi meeldetuletusi saata.",
    fleet_telegram_checking: "Kontrollimine…",
    fleet_telegram_connected: "Ühendatud",
    fleet_telegram_not_connected: "Ühendamata",
    fleet_telegram_schedule: "Kontrolli aeg",
    fleet_telegram_schedule_settings: "Seadista ajakava",
    fleet_telegram_schedule_title: "Telegrami meeldetuletuste ajakava",
    fleet_telegram_schedule_subtitle: "Ajavöönd ja selge teavituste ajakava iga riskitaseme jaoks.",
    fleet_telegram_schedule_save: "Salvesta ajakava",
    fleet_telegram_schedule_cancel: "Tühista",
    fleet_telegram_schedule_general: "Üldine",
    fleet_telegram_schedule_yellow: "Kollane režiim",
    fleet_telegram_schedule_red: "Punane režiim",
    fleet_telegram_schedule_green: "Roheline → kollane",
    fleet_telegram_schedule_yellow_hint: "Pärast esimest teadet: 8–23 päeva jäädes üks teade nädalas.",
    fleet_telegram_schedule_red_hint: "7 päeva või vähem ning aegumise korral — teated iga päev; koguse valid ise.",
    fleet_telegram_chat_label: "Telegrami vestlus, grupp või kanal",
    fleet_telegram_chat_placeholder: "Näiteks: 123456789, -1001234567890 või @channel",
    fleet_telegram_chat_hint: "Kanali puhul lisa bot administraatoriks ja luba tal sõnumeid avaldada.",
    fleet_telegram_subscriptions: "Meeldetuletused",
    fleet_telegram_inspection: "Tehnoülevaatus",
    fleet_telegram_insurance: "Kindlustus",
    fleet_telegram_save: "Salvesta",
    fleet_telegram_sending: "Saatmine…",
    fleet_telegram_test_inspection: "Testi ülevaatust",
    fleet_telegram_test_insurance: "Testi kindlustust",
    fleet_telegram_saved: "Telegrami seaded on salvestatud.",
    fleet_telegram_load_error: "Telegrami seadeid ei õnnestunud laadida.",
    fleet_telegram_save_error: "Telegrami seadeid ei õnnestunud salvestada.",
    fleet_telegram_test_error: "Testsõnumit ei õnnestunud saata. Kontrolli boti ja saaja seadeid.",
    fleet_telegram_test_inspection_ok: "Tehnoülevaatuse testsõnum saadeti. Kontrolli Telegrami.",
    fleet_telegram_test_insurance_ok: "Kindlustuse testsõnum saadeti. Kontrolli Telegrami.",
    fleet_telegram_bot_not_configured: "Telegrami bot pole seadistatud. Lisa Bot Token CRM-i üldistes Telegrami seadetes.",
    fleet_telegram_bot_disabled: "Telegrami bot on seadistatud, kuid integratsioon on välja lülitatud. Lülita Telegram CRM-i seadetes sisse.",
    fleet_telegram_invalid_chat: "Sisesta korrektne Telegrami Chat ID, grupi/kanali ID või @kanali kasutajanimi.",
    fleet_telegram_disabled_error: "Telegram on CRM-i seadetes välja lülitatud.",
    fleet_telegram_token_missing_error: "Telegrami Bot Token pole seadistatud.",
    fleet_telegram_recipient_missing_error: "FleetOpsi Telegrami saaja pole määratud.",
    fleet_telegram_timezone: "Ajavöönd",
    fleet_telegram_green_time: "30 päeva teate aeg",
    fleet_telegram_yellow_time: "Nädalase teate aeg",
    fleet_telegram_red_start: "Punaste teadete algus",
    fleet_telegram_red_end: "Punaste teadete lõpp",
    fleet_telegram_red_count: "Punaseid teateid päevas",
    fleet_telegram_rules_title: "Meeldetuletuste kord",
    fleet_telegram_schedule_green_hint: "Üle 30 päeva — vaikus. 30 päeva jäädes saadetakse esimene teade.",
    fleet_telegram_timezone_search_hint: "Vali ajavöönd loendist või otsi linna, piirkonna või UTC-nihke järgi.",
    fleet_telegram_timezone_search_placeholder: "Otsi ajavööndit…",
    fleet_telegram_timezone_no_results: "Ajavööndit ei leitud.",
    fleet_telegram_timezone_selected_now: "Aeg valitud ajavööndis",
    fleet_telegram_timezone_local_now: "Sinu kohalik aeg",
    fleet_telegram_time_pair: "Ajavöönd: {{zone}} · sinul: {{local}}",
    fleet_telegram_local_time_short: "Sinul: {{time}}",
    fleet_telegram_green_rule: "Üle 30 päeva — vaikus; 30 päeva jäädes esimene teade kell {{time}}.",
    fleet_telegram_yellow_rule: "Pärast esimest teadet kuni 8 päeva jäämiseni — üks teade nädalas kell {{time}}.",
    fleet_telegram_red_rule: "7 päeva või vähem / aegunud — iga päev {{count}} korda vahemikus {{start}}–{{end}}, ühtlaste vahedega.",
    fleet_telegram_interval: "Intervall: ligikaudu iga {{interval}} järel.",
    fleet_telegram_interval_once: "Üks teade kell {{time}}.",
    fleet_telegram_until_action: "Teavitused jätkuvad kuni vajutatakse „Vaigista tänaseks“ või „Ülevaatus tehtud“. Tänaseks vaigistamine taastub homme automaatselt.",
    fleet_telegram_personal_connected: "Isiklik Telegram on ühendatud. Saad teateid ainult oma sõidukite kohta.",
    fleet_telegram_personal_missing: "Isiklik Telegram pole ühendatud. Oma sõidukite teadete saamiseks ühenda see lehel Minu konto.",
    fleet_telegram_global_recipient: "FleetOpsi ühine saaja superadminile ja omanikuta sõidukitele.",
    fleet_telegram_account_link: "Ava Minu konto",

  },
};

const vehicleOverdueDays = (vehicle) => {
  const raw = [vehicle?.days_remaining, vehicle?.insurance_days_remaining]
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value) && value < 0);
  if (raw.length) return Math.min(...raw);
  const insuranceState = String(vehicle?.insurance_state || "").toLowerCase();
  const insuranceStatus = String(vehicle?.insurance_status || "").toLowerCase();
  if (insuranceState === "expired" || ["expired", "inactive", "ended"].includes(insuranceStatus)) return -1;
  return null;
};

const isVehicleOverdue = (vehicle) => vehicleOverdueDays(vehicle) !== null;

const overallFleetState = (vehicle) => {
  if (isVehicleOverdue(vehicle)) return "overdue";
  const states = [vehicle?.status, vehicle?.insurance_state].filter(Boolean);
  if (states.some((state) => state === "red" || state === "expired")) return "red";
  if (states.includes("yellow")) return "yellow";
  if (states.some((state) => state === "green" || state === "ok")) return "green";
  return "unknown";
};

const fleetPriorityRank = (vehicle) => {
  const state = overallFleetState(vehicle);
  const rank = { overdue: 0, red: 1, yellow: 2, green: 3, unknown: 4 }[state] ?? 5;
  const overdueDays = vehicleOverdueDays(vehicle);
  const candidateDays = [vehicle?.days_remaining, vehicle?.insurance_days_remaining]
    .map((value) => Number(value))
    .filter(Number.isFinite);
  const soonest = candidateDays.length ? Math.min(...candidateDays) : Number.POSITIVE_INFINITY;
  return [rank, overdueDays ?? soonest];
};

const FLEET_REGISTRY_FALLBACKS = {
  ru: {
    fleet_results_count: "Показано {{count}} из {{total}}",
    fleet_filter_hint: "Фильтры применяются сразу к реестру.",
    fleet_filter_clear_all: "Сбросить фильтры",
    fleet_filter_status_label: "Статус",
    fleet_filter_status_all: "Все статусы",
    fleet_filter_vehicle_label: "Транспорт",
    fleet_filter_vehicle: "Номер, марка или модель",
    fleet_filter_vin_label: "VIN-код",
    fleet_filter_vin: "Введите VIN",
    fleet_filter_inspection_label: "Техосмотр",
    fleet_filter_inspection: "Дата, дни или статус",
    fleet_filter_insurance_label: "Страховка",
    fleet_filter_insurance: "Страховщик, полис или статус",
    fleet_filter_no_results: "По выбранным фильтрам ничего не найдено.",
    fleet_column_status: "Статус",
    fleet_column_vehicle: "Транспорт",
    fleet_column_vin: "VIN-код",
    fleet_column_inspection: "Техосмотр",
    fleet_column_insurance: "Страховка",
    fleet_column_actions: "Действия",
    fleet_overall_green: "В порядке",
    fleet_overall_yellow: "Требует внимания",
    fleet_overall_red: "Критично",
    fleet_overall_overdue: "Просрочено",
    fleet_overdue_priority: "Приоритет №1",
    fleet_overall_unknown: "Нужна проверка",
    fleet_status_inspection_short: "ТО",
    fleet_status_insurance_short: "Страховка",
    fleet_days_remaining_full: "Осталось {{count}} дн.",
    fleet_days_overdue_full: "Просрочено на {{count}} дн.",
    fleet_insurance_policy_short: "Полис {{number}}",
    fleet_insurance_until_short: "до {{date}}",
    fleet_registry_check: "Проверить",
    fleet_check_modal_eyebrow: "Проверка в реестре",
    fleet_check_modal_title: "Проверить транспорт",
    fleet_check_modal_hint: "Номер и VIN выбранной машины уже подставлены. Скопируйте нужное значение и откройте официальный Transpordiamet.",
    fleet_check_registration: "Регистрационный номер",
    fleet_check_vin: "VIN-код",
    fleet_check_copy: "Копировать",
    fleet_check_copied: "Скопировано",
    fleet_check_open: "Открыть Transpordiamet",
    fleet_check_copy_open: "Скопировать номер и открыть",
    fleet_check_external_note: "Браузер не разрешает FleetOps автоматически заполнять поля на другом домене. Поэтому данные подготовлены здесь и копируются одной кнопкой.",
    fleet_check_close: "Закрыть",
    fleet_change_data_short: "Изменить",
    fleet_history_short: "История",
    fleet_copy_registration: "Копировать номер",
    fleet_copy_vin: "Копировать VIN",
    fleet_rows_per_page: "Показывать по",
    fleet_previous_page: "Предыдущая страница",
    fleet_next_page: "Следующая страница",
    fleet_select: "Выбрать",
    fleet_selected_count: "Выбрано: {{count}}",
    fleet_delete_selected: "Удалить выбранные",
    fleet_delete_all: "Удалить все",
    fleet_delete_selected_confirm: "Удалить выбранные машины ({{count}}) полностью? История и связанные Telegram-карточки этих машин также будут удалены.",
    fleet_delete_all_word: "УДАЛИТЬ",
    fleet_delete_all_confirm: "Это полностью удалит все доступные вам машины из автопарка. Для подтверждения введите: {{word}}",
    fleet_selected_deleted: "Удалено машин: {{count}}",
    fleet_all_deleted: "Автопарк очищен. Удалено машин: {{count}}",
    fleet_error_delete_selected: "Не удалось удалить выбранные машины.",
    fleet_error_delete_all: "Не удалось очистить автопарк.",
    fleet_pagination_range: "{{from}}–{{to}} из {{total}}",
  },
  en: {
    fleet_results_count: "Showing {{count}} of {{total}}",
    fleet_filter_hint: "Filters are applied to the registry instantly.",
    fleet_filter_clear_all: "Clear filters",
    fleet_filter_status_label: "Status",
    fleet_filter_status_all: "All statuses",
    fleet_filter_vehicle_label: "Vehicle",
    fleet_filter_vehicle: "Plate, make or model",
    fleet_filter_vin_label: "VIN",
    fleet_filter_vin: "Enter VIN",
    fleet_filter_inspection_label: "Inspection",
    fleet_filter_inspection: "Date, days or status",
    fleet_filter_insurance_label: "Insurance",
    fleet_filter_insurance: "Insurer, policy or status",
    fleet_filter_no_results: "No vehicles match the selected filters.",
    fleet_column_status: "Status",
    fleet_column_vehicle: "Vehicle",
    fleet_column_vin: "VIN",
    fleet_column_inspection: "Inspection",
    fleet_column_insurance: "Insurance",
    fleet_column_actions: "Actions",
    fleet_overall_green: "OK",
    fleet_overall_yellow: "Needs attention",
    fleet_overall_red: "Critical",
    fleet_overall_overdue: "Overdue",
    fleet_overdue_priority: "Priority #1",
    fleet_overall_unknown: "Review needed",
    fleet_status_inspection_short: "Inspection",
    fleet_status_insurance_short: "Insurance",
    fleet_days_remaining_full: "{{count}} days left",
    fleet_days_overdue_full: "Overdue by {{count}} days",
    fleet_insurance_policy_short: "Policy {{number}}",
    fleet_insurance_until_short: "until {{date}}",
    fleet_registry_check: "Check",
    fleet_check_modal_eyebrow: "Registry check",
    fleet_check_modal_title: "Check vehicle",
    fleet_check_modal_hint: "The selected vehicle registration number and VIN are already filled in. Copy either value and open the official Transpordiamet service.",
    fleet_check_registration: "Registration number",
    fleet_check_vin: "VIN",
    fleet_check_copy: "Copy",
    fleet_check_copied: "Copied",
    fleet_check_open: "Open Transpordiamet",
    fleet_check_copy_open: "Copy plate and open",
    fleet_check_external_note: "Browser security does not allow FleetOps to fill fields on another domain automatically. The values are prepared here for one-click copying.",
    fleet_check_close: "Close",
    fleet_change_data_short: "Edit",
    fleet_history_short: "History",
    fleet_copy_registration: "Copy registration number",
    fleet_copy_vin: "Copy VIN",
    fleet_rows_per_page: "Rows per page",
    fleet_previous_page: "Previous page",
    fleet_next_page: "Next page",
    fleet_select: "Select",
    fleet_selected_count: "Selected: {{count}}",
    fleet_delete_selected: "Delete selected",
    fleet_delete_all: "Delete all",
    fleet_delete_selected_confirm: "Permanently delete the selected vehicles ({{count}})? Their history and linked Telegram cards will also be removed.",
    fleet_delete_all_word: "DELETE",
    fleet_delete_all_confirm: "This will permanently delete every vehicle available to you. To confirm, enter: {{word}}",
    fleet_selected_deleted: "Vehicles deleted: {{count}}",
    fleet_all_deleted: "Fleet cleared. Vehicles deleted: {{count}}",
    fleet_error_delete_selected: "Could not delete the selected vehicles.",
    fleet_error_delete_all: "Could not clear the fleet.",
    fleet_pagination_range: "{{from}}–{{to}} of {{total}}",
  },
  et: {
    fleet_results_count: "Kuvatud {{count}} / {{total}}",
    fleet_filter_hint: "Filtrid rakenduvad registrile kohe.",
    fleet_filter_clear_all: "Tühjenda filtrid",
    fleet_filter_status_label: "Olek",
    fleet_filter_status_all: "Kõik olekud",
    fleet_filter_vehicle_label: "Sõiduk",
    fleet_filter_vehicle: "Number, mark või mudel",
    fleet_filter_vin_label: "VIN-kood",
    fleet_filter_vin: "Sisesta VIN",
    fleet_filter_inspection_label: "Tehnoülevaatus",
    fleet_filter_inspection: "Kuupäev, päevad või olek",
    fleet_filter_insurance_label: "Kindlustus",
    fleet_filter_insurance: "Kindlustaja, poliis või olek",
    fleet_filter_no_results: "Valitud filtritega sõidukeid ei leitud.",
    fleet_column_status: "Olek",
    fleet_column_vehicle: "Sõiduk",
    fleet_column_vin: "VIN-kood",
    fleet_column_inspection: "Tehnoülevaatus",
    fleet_column_insurance: "Kindlustus",
    fleet_column_actions: "Toimingud",
    fleet_overall_green: "Korras",
    fleet_overall_yellow: "Vajab tähelepanu",
    fleet_overall_red: "Kriitiline",
    fleet_overall_overdue: "Aegunud",
    fleet_overdue_priority: "Prioriteet nr 1",
    fleet_overall_unknown: "Vajab kontrolli",
    fleet_status_inspection_short: "Ülevaatus",
    fleet_status_insurance_short: "Kindlustus",
    fleet_days_remaining_full: "Jäänud {{count}} päeva",
    fleet_days_overdue_full: "Hilinenud {{count}} päeva",
    fleet_insurance_policy_short: "Poliis {{number}}",
    fleet_insurance_until_short: "kuni {{date}}",
    fleet_registry_check: "Kontrolli",
    fleet_check_modal_eyebrow: "Registrikontroll",
    fleet_check_modal_title: "Kontrolli sõidukit",
    fleet_check_modal_hint: "Valitud sõiduki registreerimisnumber ja VIN on juba täidetud. Kopeeri vajalik väärtus ja ava ametlik Transpordiameti teenus.",
    fleet_check_registration: "Registreerimisnumber",
    fleet_check_vin: "VIN-kood",
    fleet_check_copy: "Kopeeri",
    fleet_check_copied: "Kopeeritud",
    fleet_check_open: "Ava Transpordiamet",
    fleet_check_copy_open: "Kopeeri number ja ava",
    fleet_check_external_note: "Brauseri turvareeglid ei luba FleetOpsil teise domeeni välju automaatselt täita. Väärtused on siin ühe klõpsuga kopeerimiseks valmis.",
    fleet_check_close: "Sulge",
    fleet_change_data_short: "Muuda",
    fleet_history_short: "Ajalugu",
    fleet_copy_registration: "Kopeeri registreerimisnumber",
    fleet_copy_vin: "Kopeeri VIN",
    fleet_rows_per_page: "Ridu lehel",
    fleet_previous_page: "Eelmine leht",
    fleet_next_page: "Järgmine leht",
    fleet_select: "Vali",
    fleet_selected_count: "Valitud: {{count}}",
    fleet_delete_selected: "Kustuta valitud",
    fleet_delete_all: "Kustuta kõik",
    fleet_delete_selected_confirm: "Kas kustutada valitud sõidukid ({{count}}) jäädavalt? Kustutatakse ka nende ajalugu ja seotud Telegrami kaardid.",
    fleet_delete_all_word: "KUSTUTA",
    fleet_delete_all_confirm: "See kustutab jäädavalt kõik sulle nähtavad sõidukid. Kinnitamiseks sisesta: {{word}}",
    fleet_selected_deleted: "Kustutatud sõidukeid: {{count}}",
    fleet_all_deleted: "Autopark tühjendatud. Kustutatud sõidukeid: {{count}}",
    fleet_error_delete_selected: "Valitud sõidukite kustutamine ebaõnnestus.",
    fleet_error_delete_all: "Autopargi tühjendamine ebaõnnestus.",
    fleet_pagination_range: "{{from}}–{{to}} / {{total}}",
  },
};


const FLEET_TIMELINE_FALLBACKS = {
  ru: {
    fleet_timeline_action_vehicle_manual_verified: "Транспорт добавлен",
    fleet_timeline_desc_vehicle_manual_verified: "Транспорт {{registration}} добавлен. Срок техосмотра подтверждён.",
    fleet_timeline_action_vehicle_created: "Транспорт создан",
    fleet_timeline_desc_vehicle_created: "Транспорт {{registration}} добавлен в реестр FleetOps.",
    fleet_timeline_action_vehicle_created_from_registry: "Импорт из реестра",
    fleet_timeline_desc_vehicle_created_from_registry: "Транспорт {{registration}} импортирован из внешнего реестра.",
    fleet_timeline_action_vehicle_updated: "Данные транспорта изменены",
    fleet_timeline_desc_vehicle_updated: "Данные транспорта {{registration}} были изменены.",
    fleet_timeline_action_vehicle_compliance_updated: "Данные контроля обновлены",
    fleet_timeline_desc_vehicle_compliance_updated: "Данные техосмотра или страховки для {{registration}} обновлены.",
    fleet_timeline_action_vehicle_registry_synced: "Данные синхронизированы",
    fleet_timeline_desc_vehicle_registry_synced: "Данные {{registration}} синхронизированы с реестром.",
    fleet_timeline_action_inspection_manual_updated: "Срок техосмотра изменён",
    fleet_timeline_desc_inspection_manual_updated: "Срок техосмотра для {{registration}} обновлён.",
    fleet_timeline_action_inspection_screenshot_parsed: "Скриншот распознан",
    fleet_timeline_desc_inspection_screenshot_parsed: "Скриншот техосмотра для {{registration}} обработан.",
    fleet_timeline_action_reminder_stop: "Напоминания приостановлены",
    fleet_timeline_desc_reminder_stop: "Напоминания для {{registration}} отключены до следующего обновления данных.",
    fleet_timeline_action_reminder_normal: "Напоминания включены",
    fleet_timeline_desc_reminder_normal: "Обычный режим напоминаний для {{registration}} восстановлен.",
  },
  en: {
    fleet_timeline_action_vehicle_manual_verified: "Vehicle added",
    fleet_timeline_desc_vehicle_manual_verified: "Vehicle {{registration}} was added with a verified inspection deadline.",
    fleet_timeline_action_vehicle_created: "Vehicle created",
    fleet_timeline_desc_vehicle_created: "Vehicle {{registration}} was added to FleetOps.",
    fleet_timeline_action_vehicle_created_from_registry: "Imported from registry",
    fleet_timeline_desc_vehicle_created_from_registry: "Vehicle {{registration}} was imported from an external registry.",
    fleet_timeline_action_vehicle_updated: "Vehicle data changed",
    fleet_timeline_desc_vehicle_updated: "Vehicle data for {{registration}} was updated.",
    fleet_timeline_action_vehicle_compliance_updated: "Compliance data updated",
    fleet_timeline_desc_vehicle_compliance_updated: "Inspection or insurance data for {{registration}} was updated.",
    fleet_timeline_action_vehicle_registry_synced: "Registry data synchronized",
    fleet_timeline_desc_vehicle_registry_synced: "Vehicle {{registration}} was synchronized with the registry.",
    fleet_timeline_action_inspection_manual_updated: "Inspection deadline changed",
    fleet_timeline_desc_inspection_manual_updated: "Inspection deadline for {{registration}} was updated.",
    fleet_timeline_action_inspection_screenshot_parsed: "Screenshot recognized",
    fleet_timeline_desc_inspection_screenshot_parsed: "Inspection screenshot for {{registration}} was processed.",
    fleet_timeline_action_reminder_stop: "Reminders paused",
    fleet_timeline_desc_reminder_stop: "Reminders for {{registration}} are paused until the next data update.",
    fleet_timeline_action_reminder_normal: "Reminders enabled",
    fleet_timeline_desc_reminder_normal: "Normal reminder mode for {{registration}} was restored.",
  },
  et: {
    fleet_timeline_action_vehicle_manual_verified: "Sõiduk lisatud",
    fleet_timeline_desc_vehicle_manual_verified: "Sõiduk {{registration}} lisati kinnitatud tehnoülevaatuse tähtajaga.",
    fleet_timeline_action_vehicle_created: "Sõiduk loodud",
    fleet_timeline_desc_vehicle_created: "Sõiduk {{registration}} lisati FleetOpsi registrisse.",
    fleet_timeline_action_vehicle_created_from_registry: "Imporditud registrist",
    fleet_timeline_desc_vehicle_created_from_registry: "Sõiduk {{registration}} imporditi välisest registrist.",
    fleet_timeline_action_vehicle_updated: "Sõiduki andmed muudetud",
    fleet_timeline_desc_vehicle_updated: "Sõiduki {{registration}} andmeid muudeti.",
    fleet_timeline_action_vehicle_compliance_updated: "Kontrollandmed uuendatud",
    fleet_timeline_desc_vehicle_compliance_updated: "Sõiduki {{registration}} ülevaatuse või kindlustuse andmeid uuendati.",
    fleet_timeline_action_vehicle_registry_synced: "Registriandmed sünkroonitud",
    fleet_timeline_desc_vehicle_registry_synced: "Sõiduki {{registration}} andmed sünkrooniti registriga.",
    fleet_timeline_action_inspection_manual_updated: "Ülevaatuse tähtaeg muudetud",
    fleet_timeline_desc_inspection_manual_updated: "Sõiduki {{registration}} tehnoülevaatuse tähtaega uuendati.",
    fleet_timeline_action_inspection_screenshot_parsed: "Ekraanipilt tuvastatud",
    fleet_timeline_desc_inspection_screenshot_parsed: "Sõiduki {{registration}} tehnoülevaatuse ekraanipilt töödeldi.",
    fleet_timeline_action_reminder_stop: "Meeldetuletused peatatud",
    fleet_timeline_desc_reminder_stop: "Sõiduki {{registration}} meeldetuletused on peatatud kuni järgmise andmeuuenduseni.",
    fleet_timeline_action_reminder_normal: "Meeldetuletused lubatud",
    fleet_timeline_desc_reminder_normal: "Sõiduki {{registration}} tavapärane meeldetuletusrežiim taastati.",
  },
};

const timelineActionSuffix = (action) =>
  String(action || "unknown")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");


const FALLBACK_TIMEZONES = [
  "UTC", "Europe/Tallinn", "Europe/Helsinki", "Europe/Riga", "Europe/Vilnius",
  "Europe/Stockholm", "Europe/Warsaw", "Europe/Berlin", "Europe/Paris", "Europe/London",
  "Europe/Kyiv", "Europe/Athens", "Europe/Istanbul", "Asia/Dubai", "Asia/Tbilisi",
  "Asia/Jerusalem", "Asia/Kolkata", "Asia/Bangkok", "Asia/Singapore", "Asia/Tokyo",
  "Australia/Sydney", "America/New_York", "America/Chicago", "America/Denver",
  "America/Los_Angeles", "America/Toronto", "America/Vancouver", "America/Sao_Paulo",
];

const browserTimeZone = () => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; }
  catch { return "UTC"; }
};

const safeZoneTime = (date, timeZone, locale = "ru") => {
  try {
    return new Intl.DateTimeFormat(locale, {
      timeZone,
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).format(date);
  } catch {
    return "—";
  }
};

const timeZoneOffsetMinutes = (date, timeZone) => {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
      hourCycle: "h23",
    }).formatToParts(date);
    const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    const asUtc = Date.UTC(
      Number(values.year), Number(values.month) - 1, Number(values.day),
      Number(values.hour), Number(values.minute), Number(values.second)
    );
    return Math.round((asUtc - date.getTime()) / 60000);
  } catch {
    return null;
  }
};

const localEquivalentTime = (hhmm, timeZone) => {
  const match = String(hhmm || "").match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return "—";
  const now = new Date();
  const zoneOffset = timeZoneOffsetMinutes(now, timeZone);
  if (zoneOffset === null) return "—";
  const localOffset = -now.getTimezoneOffset();
  const sourceMinutes = Number(match[1]) * 60 + Number(match[2]);
  const localMinutes = ((sourceMinutes - zoneOffset + localOffset) % 1440 + 1440) % 1440;
  return `${String(Math.floor(localMinutes / 60)).padStart(2, "0")}:${String(localMinutes % 60).padStart(2, "0")}`;
};

const timeZoneOffsetLabel = (date, timeZone) => {
  const minutes = timeZoneOffsetMinutes(date, timeZone);
  if (minutes === null) return "UTC";
  if (minutes === 0) return "UTC";
  const sign = minutes >= 0 ? "+" : "−";
  const absolute = Math.abs(minutes);
  return `UTC${sign}${String(Math.floor(absolute / 60)).padStart(2, "0")}:${String(absolute % 60).padStart(2, "0")}`;
};

const timeZoneSearchLabel = (zone) =>
  String(zone || "")
    .replace(/_/g, " ")
    .replace(/\//g, " ");

function TimeZonePicker({
  value,
  options,
  onChange,
  now,
  locale,
  label,
  searchPlaceholder,
  emptyText,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (!wrapRef.current?.contains(event.target)) setOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    const frame = window.requestAnimationFrame(() => searchRef.current?.focus());

    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const normalizedSearch = search.trim().toLowerCase();
  const filteredOptions = useMemo(() => {
    if (!normalizedSearch) return options;
    return options.filter((zone) => {
      const offset = timeZoneOffsetLabel(now, zone).toLowerCase();
      const searchable = `${zone} ${timeZoneSearchLabel(zone)} ${offset}`.toLowerCase();
      return searchable.includes(normalizedSearch);
    });
  }, [normalizedSearch, now, options]);

  const choose = (zone) => {
    onChange(zone);
    setSearch("");
    setOpen(false);
  };

  const selectedZone = value || "UTC";

  return (
    <div className={s.timeZonePicker} ref={wrapRef}>
      <button
        type="button"
        className={`${s.timeZonePickerButton} ${open ? s.timeZonePickerButtonOpen : ""}`}
        onClick={() => setOpen((current) => !current)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
      >
        <span className={s.timeZonePickerSelected}>
          <strong>{selectedZone}</strong>
          <small>{timeZoneOffsetLabel(now, selectedZone)}</small>
        </span>
        <span className={s.timeZonePickerNow}>{safeZoneTime(now, selectedZone, locale)}</span>
        <span className={s.timeZonePickerChevron} aria-hidden="true">{open ? "⌃" : "⌄"}</span>
      </button>

      {open && (
        <div className={s.timeZonePickerMenu}>
          <div className={s.timeZonePickerSearchWrap}>
            <span aria-hidden="true">⌕</span>
            <input
              ref={searchRef}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
            />
          </div>

          <div className={s.timeZonePickerList} role="listbox" aria-label={label}>
            {filteredOptions.length ? filteredOptions.map((zone) => (
              <button
                type="button"
                role="option"
                aria-selected={zone === selectedZone}
                className={`${s.timeZonePickerOption} ${zone === selectedZone ? s.timeZonePickerOptionActive : ""}`}
                key={zone}
                onClick={() => choose(zone)}
              >
                <span className={s.timeZonePickerOptionMain}>
                  <strong>{zone}</strong>
                  <small>{timeZoneOffsetLabel(now, zone)}</small>
                </span>
                <span className={s.timeZonePickerOptionTime}>{safeZoneTime(now, zone, locale)}</span>
                <span className={s.timeZonePickerOptionCheck} aria-hidden="true">{zone === selectedZone ? "✓" : ""}</span>
              </button>
            )) : (
              <div className={s.timeZonePickerEmpty}>{emptyText}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function FleetControlPage() {
  const { t, i18n } = useTranslation();
  const activeLanguage = String(i18n.resolvedLanguage || i18n.language || "ru")
    .toLowerCase()
    .split("-")[0];
  const modalText = useCallback(
    (key, options = {}) =>
      t(key, {
        defaultValue:
          FLEET_MODAL_FALLBACKS[activeLanguage]?.[key] ||
          FLEET_MODAL_FALLBACKS.en[key] ||
          key,
        ...options,
      }),
    [t, activeLanguage]
  );
  const registryText = useCallback(
    (key, options = {}) =>
      t(key, {
        defaultValue:
          FLEET_REGISTRY_FALLBACKS[activeLanguage]?.[key] ||
          FLEET_REGISTRY_FALLBACKS.en[key] ||
          key,
        ...options,
      }),
    [t, activeLanguage]
  );

  const telegramText = useCallback(
    (key, options = {}) =>
      t(key, {
        defaultValue:
          FLEET_TELEGRAM_FALLBACKS[activeLanguage]?.[key] ||
          FLEET_TELEGRAM_FALLBACKS.en[key] ||
          key,
        ...options,
      }),
    [t, activeLanguage]
  );
  const telegramErrorText = useCallback(
    (detail, fallbackKey) => {
      const raw = String(detail || "");
      const lower = raw.toLowerCase();
      if (lower.includes("numeric chat id") || lower.includes("@channelusername")) {
        return telegramText("fleet_telegram_invalid_chat");
      }
      if (lower.includes("telegram is disabled")) {
        return telegramText("fleet_telegram_disabled_error");
      }
      if (lower.includes("bot token") && lower.includes("not configured")) {
        return telegramText("fleet_telegram_token_missing_error");
      }
      if (lower.includes("chat/channel") && lower.includes("not configured")) {
        return telegramText("fleet_telegram_recipient_missing_error");
      }
      if (lower.includes("personal telegram") && lower.includes("not linked")) {
        return telegramText("fleet_telegram_personal_missing");
      }
      if (lower.includes("telegram test failed")) {
        return telegramText("fleet_telegram_test_error");
      }
      return raw || telegramText(fallbackKey);
    },
    [telegramText]
  );
  const [vehicles, setVehicles] = useState([]);
  const [scopeUserId, setScopeUserId] = useState(() => {
    try { return sessionStorage.getItem("fleetops:superadmin-scope-user") || ""; } catch { return ""; }
  });
  const changeScopeUser = useCallback((value) => {
    const next = value ? String(value) : "";
    setScopeUserId(next);
    setSelectedVehicleIds(new Set());
    setPage(1);
    try { sessionStorage.setItem("fleetops:superadmin-scope-user", next); } catch {}
  }, []);
  const [selectedVehicleIds, setSelectedVehicleIds] = useState(new Set());
  const [deletingVehicles, setDeletingVehicles] = useState(false);
  const [columnFilters, setColumnFilters] = useState({
    status: "",
    vehicle: "",
    vin: "",
    inspection: "",
    insurance: "",
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [copiedKey, setCopiedKey] = useState("");
  const [registryCheckVehicle, setRegistryCheckVehicle] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [drawer, setDrawer] = useState(null);
  const timelineText = useCallback(
    (kind, event, options = {}) => {
      const suffix = timelineActionSuffix(event?.action);
      const key = `fleet_timeline_${kind}_${suffix}`;
      return t(key, {
        defaultValue:
          FLEET_TIMELINE_FALLBACKS[activeLanguage]?.[key] ||
          FLEET_TIMELINE_FALLBACKS.en[key] ||
          (kind === "action" ? String(event?.action || "") : String(event?.description || "")),
        registration: drawer?.registration_number || "",
        ...options,
      });
    },
    [t, activeLanguage, drawer]
  );

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [insuranceExpanded, setInsuranceExpanded] = useState(false);
  const [modalError, setModalError] = useState("");

  const [registrationNumber, setRegistrationNumber] = useState("");
  const [vin, setVin] = useState("");
  const [validUntil, setValidUntil] = useState("");
  const [inspectionDate, setInspectionDate] = useState("");
  const [insuranceDocumentNumber, setInsuranceDocumentNumber] = useState("");
  const [insuranceProvider, setInsuranceProvider] = useState("");
  const [insuranceType, setInsuranceType] = useState("");
  const [insuranceValidFrom, setInsuranceValidFrom] = useState("");
  const [insuranceValidUntil, setInsuranceValidUntil] = useState("");
  const [insuranceStatus, setInsuranceStatus] = useState("unknown");

  const [editInspection, setEditInspection] = useState(null);
  const [newValidUntil, setNewValidUntil] = useState("");
  const [newInspectionDate, setNewInspectionDate] = useState("");
  const [newInsuranceDocumentNumber, setNewInsuranceDocumentNumber] = useState("");
  const [newInsuranceProvider, setNewInsuranceProvider] = useState("");
  const [newInsuranceType, setNewInsuranceType] = useState("");
  const [newInsuranceValidFrom, setNewInsuranceValidFrom] = useState("");
  const [newInsuranceValidUntil, setNewInsuranceValidUntil] = useState("");
  const [newInsuranceStatus, setNewInsuranceStatus] = useState("unknown");

  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrResult, setOcrResult] = useState(null);
  const [ocrFileName, setOcrFileName] = useState("");
  const [ocrError, setOcrError] = useState("");
  const [ocrProgress, setOcrProgress] = useState(0);
  const [batchItems, setBatchItems] = useState([]);
  const [batchProcessing, setBatchProcessing] = useState(false);
  const [batchSaving, setBatchSaving] = useState(false);
  const [batchDragActive, setBatchDragActive] = useState(false);
  const [batchFlow, setBatchFlow] = useState({ active: false, phase: "upload", percent: 0, done: 0, total: 0, current: "" });
  const [deepLinkHandled, setDeepLinkHandled] = useState(false);
  const [telegramStatus, setTelegramStatus] = useState(null);
  const [telegramForm, setTelegramForm] = useState({
    chat_id: "",
    inspection_enabled: true,
    insurance_enabled: true,
    timezone: "Europe/Tallinn",
    green_time: "12:00",
    yellow_time: "12:00",
    red_window_start: "12:00",
    red_window_end: "18:00",
    red_times_per_day: 1,
  });
  const [telegramLoading, setTelegramLoading] = useState(true);
  const [telegramSaving, setTelegramSaving] = useState(false);
  const [telegramTesting, setTelegramTesting] = useState("");
  const [telegramFeedback, setTelegramFeedback] = useState(null);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [rulesInfoOpen, setRulesInfoOpen] = useState(false);
  const rulesInfoRef = useRef(null);
  const [scheduleDraft, setScheduleDraft] = useState({
    timezone: "Europe/Tallinn",
    green_time: "12:00",
    yellow_time: "12:00",
    red_window_start: "12:00",
    red_window_end: "18:00",
    red_times_per_day: 1,
  });
  const [clockTick, setClockTick] = useState(() => Date.now());
  const localTimeZone = useMemo(() => browserTimeZone(), []);
  const timeZoneOptions = useMemo(() => {
    try {
      const supported = typeof Intl.supportedValuesOf === "function"
        ? Intl.supportedValuesOf("timeZone")
        : [];
      return Array.from(new Set(["UTC", ...supported, ...FALLBACK_TIMEZONES]));
    } catch {
      return FALLBACK_TIMEZONES;
    }
  }, []);
  useEffect(() => {
    const timer = window.setInterval(() => setClockTick(Date.now()), 30000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!rulesInfoOpen) return undefined;

    const handleOutsidePointer = (event) => {
      if (rulesInfoRef.current && !rulesInfoRef.current.contains(event.target)) {
        setRulesInfoOpen(false);
      }
    };
    const handleEscape = (event) => {
      if (event.key === "Escape") setRulesInfoOpen(false);
    };

    document.addEventListener("pointerdown", handleOutsidePointer);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("pointerdown", handleOutsidePointer);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [rulesInfoOpen]);
  const activeSchedule = scheduleOpen ? scheduleDraft : telegramForm;
  const selectedZoneNow = useMemo(
    () => safeZoneTime(new Date(clockTick), activeSchedule.timezone || "Europe/Tallinn", activeLanguage),
    [clockTick, activeSchedule.timezone, activeLanguage]
  );
  const localZoneNow = useMemo(
    () => safeZoneTime(new Date(clockTick), localTimeZone, activeLanguage),
    [clockTick, localTimeZone, activeLanguage]
  );
  const scheduleTimePreview = useCallback((value) => ({
    zone: value || "12:00",
    local: localEquivalentTime(value || "12:00", activeSchedule.timezone || "Europe/Tallinn"),
  }), [activeSchedule.timezone]);

  const redReminderIntervalMinutes = useMemo(() => {
    const toMinutes = (value) => {
      const [hour, minute] = String(value || "00:00").split(":").map(Number);
      return (Number.isFinite(hour) ? hour : 0) * 60 + (Number.isFinite(minute) ? minute : 0);
    };
    const source = scheduleOpen ? scheduleDraft : telegramForm;
    const count = Math.max(1, Number(source.red_times_per_day || 1));
    if (count <= 1) return 0;
    const start = toMinutes(source.red_window_start);
    const end = toMinutes(source.red_window_end);
    if (end <= start) return 0;
    return Math.round((end - start) / (count - 1));
  }, [scheduleOpen, scheduleDraft, telegramForm]);

  const reminderIntervalLabel = useMemo(() => {
    const minutes = redReminderIntervalMinutes;
    if (!minutes) return "";
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    if (activeLanguage === "ru") return [hours ? `${hours} ч` : "", rest ? `${rest} мин` : ""].filter(Boolean).join(" ");
    if (activeLanguage === "et") return [hours ? `${hours} h` : "", rest ? `${rest} min` : ""].filter(Boolean).join(" ");
    return [hours ? `${hours} h` : "", rest ? `${rest} min` : ""].filter(Boolean).join(" ");
  }, [redReminderIntervalMinutes, activeLanguage]);

  const canTestTelegram = Boolean(
    telegramStatus?.bot_configured && (
      telegramStatus?.recipient_mode === "personal"
        ? telegramStatus?.personal_recipient_configured
        : String(telegramForm.chat_id || "").trim()
    )
  );

  const normalizedRegistration = normalizeIdentifier(registrationNumber);
  const normalizedVin = normalizeIdentifier(vin);
  const registrationValid = isPlate(normalizedRegistration);
  const vinValid = !normalizedVin || isVin(normalizedVin);
  const canSaveVehicle = registrationValid && vinValid && Boolean(validUntil);

  const load = useCallback(
    async (search = "") => {
      setLoading(true);
      setError("");
      try {
        setVehicles((await A.vehicles(search.trim(), scopeUserId)).data || []);
      } catch (e) {
        setError(e.response?.data?.detail || t("fleet_error_load"));
      } finally {
        setLoading(false);
      }
    },
    [t, scopeUserId]
  );

  useEffect(() => {
    load("");
  }, [load]);

  const loadTelegramStatus = useCallback(async () => {
    setTelegramLoading(true);
    try {
      const data = (await A.telegramStatus(scopeUserId)).data || {};
      setTelegramStatus(data);
      setTelegramForm({
        chat_id: data.chat_id || "",
        inspection_enabled: data.inspection_enabled !== false,
        insurance_enabled: data.insurance_enabled !== false,
        timezone: data.timezone || "Europe/Tallinn",
        green_time: data.green_time || data.yellow_time || data.schedule_time || "12:00",
        yellow_time: data.yellow_time || data.schedule_time || "12:00",
        red_window_start: data.red_window_start || "12:00",
        red_window_end: data.red_window_end || "18:00",
        red_times_per_day: Number(data.red_times_per_day || 1),
      });
    } catch (e) {
      setTelegramFeedback({
        type: "error",
        text: telegramErrorText(e.response?.data?.detail, "fleet_telegram_load_error"),
      });
    } finally {
      setTelegramLoading(false);
    }
  }, [telegramErrorText, scopeUserId]);

  useEffect(() => {
    setTelegramFeedback(null);
    setScheduleOpen(false);
    setRulesInfoOpen(false);
    loadTelegramStatus();
  }, [loadTelegramStatus]);

  const persistTelegramPreferences = async (payload) => {
    setTelegramSaving(true);
    setTelegramFeedback(null);
    try {
      const data = (await A.saveTelegramPreferences(payload, scopeUserId)).data || {};
      setTelegramStatus(data);
      setTelegramForm((prev) => ({
        ...prev,
        ...payload,
        chat_id: data.chat_id ?? payload.chat_id ?? prev.chat_id,
        inspection_enabled: data.inspection_enabled ?? payload.inspection_enabled ?? prev.inspection_enabled,
        insurance_enabled: data.insurance_enabled ?? payload.insurance_enabled ?? prev.insurance_enabled,
        timezone: data.timezone || payload.timezone || prev.timezone,
        green_time: data.green_time || payload.green_time || prev.green_time,
        yellow_time: data.yellow_time || data.schedule_time || payload.yellow_time || prev.yellow_time,
        red_window_start: data.red_window_start || payload.red_window_start || prev.red_window_start,
        red_window_end: data.red_window_end || payload.red_window_end || prev.red_window_end,
        red_times_per_day: Number(data.red_times_per_day || payload.red_times_per_day || prev.red_times_per_day || 1),
      }));
      setTelegramFeedback({ type: "success", text: telegramText("fleet_telegram_saved") });
      return true;
    } catch (e) {
      setTelegramFeedback({
        type: "error",
        text: telegramErrorText(e.response?.data?.detail, "fleet_telegram_save_error"),
      });
      return false;
    } finally {
      setTelegramSaving(false);
    }
  };

  const saveTelegramPreferences = async () => {
    await persistTelegramPreferences(telegramForm);
  };

  const openScheduleSettings = () => {
    setTelegramFeedback(null);
    setRulesInfoOpen(false);
    setScheduleDraft({
      timezone: telegramForm.timezone || "Europe/Tallinn",
      green_time: telegramForm.green_time || "12:00",
      yellow_time: telegramForm.yellow_time || "12:00",
      red_window_start: telegramForm.red_window_start || "12:00",
      red_window_end: telegramForm.red_window_end || "18:00",
      red_times_per_day: Number(telegramForm.red_times_per_day || 1),
    });
    setScheduleOpen(true);
  };

  const saveSchedulePreferences = async () => {
    const payload = { ...telegramForm, ...scheduleDraft };
    const saved = await persistTelegramPreferences(payload);
    if (saved) setScheduleOpen(false);
  };

  useEffect(() => {
    if (!scheduleOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape" && !telegramSaving) setScheduleOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [scheduleOpen, telegramSaving]);

  const testTelegram = async (kind) => {
    setTelegramTesting(kind);
    setTelegramFeedback(null);
    try {
      const saved = (await A.saveTelegramPreferences(telegramForm, scopeUserId)).data || {};
      setTelegramStatus(saved);
      await A.testTelegram(kind, scopeUserId);
      setTelegramFeedback({
        type: "success",
        text: kind === "insurance"
          ? telegramText("fleet_telegram_test_insurance_ok")
          : telegramText("fleet_telegram_test_inspection_ok"),
      });
      await loadTelegramStatus();
    } catch (e) {
      setTelegramFeedback({
        type: "error",
        text: telegramErrorText(e.response?.data?.detail, "fleet_telegram_test_error"),
      });
    } finally {
      setTelegramTesting("");
    }
  };

  useEffect(() => {
    if (deepLinkHandled || !vehicles.length) return;
    const params = new URLSearchParams(window.location.search);
    const id = Number(params.get("vehicle_id"));
    if (id && ["inspection", "compliance"].includes(params.get("action"))) {
      const item = vehicles.find((vehicle) => Number(vehicle.id) === id);
      if (item) {
        setEditInspection(item);
        setNewValidUntil(item.inspection_valid_until || "");
        setNewInspectionDate(item.inspection_date || "");
        setNewInsuranceDocumentNumber(item.insurance_document_number || "");
        setNewInsuranceProvider(item.insurance_provider || "");
        setNewInsuranceType(item.insurance_type || "");
        setNewInsuranceValidFrom(item.insurance_valid_from || "");
        setNewInsuranceValidUntil(item.insurance_valid_until || "");
        setNewInsuranceStatus(item.insurance_status || "unknown");
        setInsuranceExpanded(Boolean(
          item.insurance_provider ||
          item.insurance_document_number ||
          item.insurance_type ||
          item.insurance_valid_from ||
          item.insurance_valid_until ||
          (item.insurance_status && item.insurance_status !== "unknown")
        ));
        setDeepLinkHandled(true);
        window.history.replaceState({}, "", window.location.pathname);
      }
    }
  }, [vehicles, deepLinkHandled]);

  const stats = useMemo(
    () => ({
      total: vehicles.length,
      due: vehicles.filter(
        (vehicle) =>
          vehicle.days_remaining >= 0 && vehicle.days_remaining <= 30
      ).length,
      critical: vehicles.filter(
        (vehicle) =>
          vehicle.days_remaining !== null && vehicle.days_remaining <= 7
      ).length,
      ok: vehicles.filter((vehicle) => vehicle.days_remaining > 30).length,
    }),
    [vehicles]
  );

  const resetOcr = () => {
    setOcrResult(null);
    setOcrFileName("");
    setOcrError("");
    setOcrProgress(0);
  };

  const resetAddForm = () => {
    setBatchItems([]);
    setBatchProcessing(false);
    setBatchSaving(false);
    setBatchDragActive(false);
    setBatchFlow({ active: false, phase: "upload", percent: 0, done: 0, total: 0, current: "" });
    setRegistrationNumber("");
    setVin("");
    setValidUntil("");
    setInspectionDate("");
    setInsuranceDocumentNumber("");
    setInsuranceProvider("");
    setInsuranceType("");
    setInsuranceValidFrom("");
    setInsuranceValidUntil("");
    setInsuranceStatus("unknown");
    setInsuranceExpanded(false);
    setModalError("");
    resetOcr();
  };

  const closeAdd = () => {
    if (saving || ocrLoading || batchProcessing || batchSaving) return;
    setShowAdd(false);
    resetAddForm();
  };

  const addManual = async (event) => {
    event?.preventDefault();
    if (!canSaveVehicle) return;

    setSaving(true);
    setModalError("");
    try {
      await A.manualCreateVehicle({
        // identifier remains for backward compatibility with older backends.
        identifier: normalizedRegistration,
        registration_number: normalizedRegistration,
        vin: normalizedVin || null,
        inspection_valid_until: validUntil,
        inspection_date: inspectionDate || null,
        insurance_document_number: insuranceDocumentNumber || null,
        insurance_provider: insuranceProvider || null,
        insurance_type: insuranceType || null,
        insurance_valid_from: insuranceValidFrom || null,
        insurance_valid_until: insuranceValidUntil || null,
        insurance_status: insuranceStatus || "unknown",
        source: ocrResult?.inspection_valid_until || ocrResult?.insurance_detected ? "screenshot_ocr" : "manual",
      }, scopeUserId);
      setShowAdd(false);
      resetAddForm();
      setSuccess(t("fleet_created"));
      await load("");
    } catch (e) {
      setModalError(e.response?.data?.detail || t("fleet_error_create"));
    } finally {
      setSaving(false);
    }
  };

  const getBatchValidation = (data) => {
    const plate = normalizePlate(data?.registration_number);
    const rawDeadline = String(data?.inspection_valid_until || "").trim();
    const validUntilValue = normalizeIsoDate(rawDeadline);

    // The browser date field already prevents an invalid visible date from
    // being entered. Do not keep an OCR row blocked because of a stale status
    // or an over-strict secondary parser. For the reminder engine the only
    // required values are the registration number and a non-empty deadline.
    // Backend/Pydantic remains the final validation boundary on save.
    const hasRegistration = plate.length >= 3;
    const hasInspectionDeadline = Boolean(rawDeadline && validUntilValue);

    return {
      ready: hasRegistration && hasInspectionDeadline,
      hasRegistration,
      hasInspectionDeadline,
      plate,
      validUntilValue,
    };
  };

  const isBatchDataReady = (data) => getBatchValidation(data).ready;

  const patchBatchItem = (id, patch) => {
    setBatchItems((items) =>
      items.map((item) => (item.id === id ? { ...item, ...patch } : item))
    );
  };

  const batchDataFromResult = (result) => ({
    registration_number: normalizePlate(result?.registration_number),
    vin: normalizeVin(result?.vin),
    make: String(result?.make || "").trim(),
    model: String(result?.model || "").trim(),
    year: result?.year || "",
    inspection_valid_until: normalizeIsoDate(result?.inspection_valid_until),
    inspection_date: normalizeIsoDate(result?.inspection_date),
    insurance_document_number: String(result?.insurance_document_number || "").trim(),
    insurance_provider: String(result?.insurance_provider || "").trim(),
    insurance_type: String(result?.insurance_type || "").trim(),
    insurance_valid_from: normalizeIsoDate(result?.insurance_valid_from),
    insurance_valid_until: normalizeIsoDate(result?.insurance_valid_until),
    insurance_status: result?.insurance_status || "unknown",
    confidence: result?.confidence || "low",
    insurance_detected: Boolean(result?.insurance_detected),
    source: result?.source || "screenshot_ocr",
  });

  const batchErrorInfo = (error) => {
    const detail = error?.response?.data?.detail;
    if (detail && typeof detail === "object") {
      return {
        message: detail.message || detail.detail || modalText("fleet_batch_error_unknown"),
        code: detail.code || "",
        sourceFileId: detail.source_file_id || "",
      };
    }
    return {
      message: (typeof detail === "string" && detail) || error?.message || modalText("fleet_batch_error_unknown"),
      code: "",
      sourceFileId: "",
    };
  };

  const updateOverallBatchProgress = (done, total, filePercent = 0, phase = "parse", current = "") => {
    const safeTotal = Math.max(1, Number(total || 1));
    const fraction = Math.min(1, Math.max(0, Number(filePercent || 0) / 100));
    const percent = Math.min(100, Math.max(0, Math.round(((Number(done || 0) + fraction) / safeTotal) * 100)));
    setBatchFlow({ active: true, phase, percent, done: Number(done || 0), total: safeTotal, current });
  };

  const parseOneBatchPdf = async (file, id, done, total) => {
    patchBatchItem(id, { status: "processing", progress: 1, error: "", errorCode: "" });
    updateOverallBatchProgress(done, total, 0, "upload", file.name);
    try {
      const response = await A.parseVehiclePdf(file, {
        keepSource: true,
        onUploadProgress: (event) => {
          const totalBytes = Number(event?.total || file.size || 0);
          const loaded = Number(event?.loaded || 0);
          const uploadPercent = totalBytes > 0 ? Math.min(100, Math.round((loaded * 100) / totalBytes)) : 0;
          patchBatchItem(id, { progress: Math.max(2, Math.round(uploadPercent * 0.45)) });
          updateOverallBatchProgress(done, total, uploadPercent * 0.45, "upload", file.name);
        },
      });
      patchBatchItem(id, { progress: 60 });
      updateOverallBatchProgress(done, total, 65, "parse", file.name);
      const data = batchDataFromResult({ ...response.data, source: "transpordiamet_pdf" });
      patchBatchItem(id, {
        status: isBatchDataReady(data) ? "ready" : "review",
        progress: 100,
        data,
        error: "",
        errorCode: "",
        sourceFileId: response?.data?.source_file_id || "",
        sourceSaved: Boolean(response?.data?.source_file_saved),
      });
      return true;
    } catch (error) {
      const info = batchErrorInfo(error);
      patchBatchItem(id, {
        status: "error",
        progress: 100,
        error: info.message,
        errorCode: info.code,
        sourceFileId: info.sourceFileId,
        sourceSaved: Boolean(info.sourceFileId),
      });
      return false;
    }
  };

  const processBatchFiles = async (fileList) => {
    if (batchProcessing || batchSaving) return;
    const all = Array.from(fileList || []);
    const isPdfFile = (file) =>
      (file.type || "").toLowerCase() === "application/pdf" ||
      String(file.name || "").toLowerCase().endsWith(".pdf");
    const isImageFile = (file) =>
      /^image\/(png|jpeg)$/.test((file.type || "").toLowerCase()) ||
      /\.(png|jpe?g)$/i.test(String(file.name || ""));
    const accepted = all
      .filter((file) => isPdfFile(file) || isImageFile(file))
      .slice(0, 50);
    if (!accepted.length) {
      setModalError(modalText("fleet_batch_images_only"));
      return;
    }

    setModalError("");
    setBatchProcessing(true);
    setBatchFlow({ active: true, phase: "upload", percent: 0, done: 0, total: accepted.length, current: accepted[0]?.name || "" });
    const stamp = Date.now();
    const created = accepted.map((file, index) => ({
      id: `${stamp}-${index}-${file.name}`,
      name: file.name,
      size: file.size,
      file,
      fileKind: isPdfFile(file) ? "pdf" : "image",
      status: "queued",
      progress: 0,
      data: {},
      error: "",
      errorCode: "",
      sourceFileId: "",
      sourceSaved: false,
    }));
    const ids = created.map((item) => item.id);
    setBatchItems((items) => [...items, ...created]);

    let completed = 0;
    try {
      // Process in source order so the progress screen always matches the row
      // the user sees in the queue. Each PDF is persisted on the backend before
      // parsing; a parser failure therefore never makes the source disappear.
      for (let index = 0; index < accepted.length; index += 1) {
        const file = accepted[index];
        const id = ids[index];
        if (isPdfFile(file)) {
          await parseOneBatchPdf(file, id, completed, accepted.length);
        } else {
          patchBatchItem(id, { status: "processing", progress: 1, error: "" });
          updateOverallBatchProgress(completed, accepted.length, 5, "parse", file.name);
          try {
            const result = await parseVehicleScreenshotInBrowser(file, (progress) => {
              patchBatchItem(id, { progress });
              updateOverallBatchProgress(completed, accepted.length, progress, "parse", file.name);
            });
            const data = batchDataFromResult({ ...result, source: "screenshot_ocr" });
            patchBatchItem(id, {
              status: isBatchDataReady(data) ? "ready" : "review",
              progress: 100,
              data,
              error: "",
            });
          } catch (error) {
            patchBatchItem(id, {
              status: "error",
              progress: 100,
              error: error?.message || t("fleet_ocr_error"),
            });
          }
        }
        completed += 1;
        updateOverallBatchProgress(completed, accepted.length, 0, "parse", file.name);
      }
    } catch (error) {
      console.error("FleetOps batch file parsing failed", error);
      setModalError(batchErrorInfo(error).message);
    } finally {
      setBatchProcessing(false);
      setBatchFlow((flow) => ({ ...flow, active: false, percent: 100, done: completed }));
    }
  };

  const retryFailedBatchFiles = async () => {
    if (batchProcessing || batchSaving) return;
    const failed = batchItems.filter((item) => ["error", "save_error"].includes(item.status) && item.file);
    if (!failed.length) return;
    setBatchProcessing(true);
    setModalError("");
    setBatchFlow({ active: true, phase: "upload", percent: 0, done: 0, total: failed.length, current: failed[0]?.name || "" });
    let completed = 0;
    try {
      for (const item of failed) {
        if (item.status === "save_error" && isBatchDataReady(item.data)) {
          patchBatchItem(item.id, { status: "ready", error: "" });
        } else if (item.fileKind === "pdf") {
          await parseOneBatchPdf(item.file, item.id, completed, failed.length);
        } else {
          patchBatchItem(item.id, { status: "processing", progress: 1, error: "" });
          try {
            const result = await parseVehicleScreenshotInBrowser(item.file, (progress) => {
              patchBatchItem(item.id, { progress });
              updateOverallBatchProgress(completed, failed.length, progress, "parse", item.name);
            });
            const data = batchDataFromResult({ ...result, source: "screenshot_ocr" });
            patchBatchItem(item.id, { status: isBatchDataReady(data) ? "ready" : "review", progress: 100, data, error: "" });
          } catch (error) {
            patchBatchItem(item.id, { status: "error", progress: 100, error: error?.message || t("fleet_ocr_error") });
          }
        }
        completed += 1;
        updateOverallBatchProgress(completed, failed.length, 0, "parse", item.name);
      }
    } finally {
      setBatchProcessing(false);
      setBatchFlow((flow) => ({ ...flow, active: false, percent: 100, done: completed }));
    }
  };

  const downloadBatchErrorReport = () => {
    const failed = batchItems.filter((item) => ["error", "save_error", "review"].includes(item.status) || item.error);
    if (!failed.length) return;
    const report = {
      generated_at: new Date().toISOString(),
      project: "FleetOps vehicle import",
      failed_count: failed.length,
      files: failed.map((item) => ({
        filename: item.name,
        size_bytes: item.size,
        type: item.fileKind,
        status: item.status,
        error_code: item.errorCode || null,
        error: item.error || null,
        source_file_id: item.sourceFileId || null,
        detected: item.data || {},
      })),
    };
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: "application/json;charset=utf-8" });
    const href = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = href;
    anchor.download = `fleetops-import-errors-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(href);
  };

  const updateBatchField = (id, field, rawValue) => {
    setBatchItems((items) =>
      items.map((item) => {
        if (item.id !== id || item.status === "saved" || item.status === "saving") return item;
        let value = rawValue;
        if (field === "registration_number") value = normalizePlate(rawValue);
        else if (field === "vin") value = normalizeVin(rawValue);
        else if (["inspection_valid_until", "inspection_date", "insurance_valid_from", "insurance_valid_until"].includes(field)) value = normalizeIsoDate(rawValue);
        const data = { ...item.data, [field]: value };
        return {
          ...item,
          data,
          error: "",
          status: isBatchDataReady(data) ? "ready" : "review",
        };
      })
    );
  };

  const removeBatchItem = (id) => {
    if (batchProcessing || batchSaving) return;
    setBatchItems((items) => items.filter((item) => item.id !== id));
  };

  const clearBatch = () => {
    if (batchProcessing || batchSaving) return;
    setBatchItems([]);
  };

  const saveBatchVehicles = async () => {
    if (batchSaving || batchProcessing) return;
    const candidates = batchItems.filter(
      (item) => item.status !== "saved" && item.status !== "saving" && isBatchDataReady(item.data)
    );
    if (!candidates.length) {
      setModalError(modalText("fleet_batch_review_hint"));
      return;
    }

    setBatchSaving(true);
    setModalError("");
    setBatchFlow({ active: true, phase: "save", percent: 0, done: 0, total: candidates.length, current: candidates[0]?.name || "" });
    let savedCount = 0;
    let processedCount = 0;

    for (const item of candidates) {
      patchBatchItem(item.id, { status: "saving", error: "" });
      const data = item.data || {};
      const validation = getBatchValidation(data);
      try {
        await A.manualCreateVehicle({
          identifier: validation.plate,
          registration_number: validation.plate,
          vin: isVin(normalizeVin(data.vin)) ? normalizeVin(data.vin) : null,
          make: data.make || null,
          model: data.model || null,
          year: data.year ? Number(data.year) : null,
          inspection_valid_until: validation.validUntilValue,
          inspection_date: data.inspection_date || null,
          insurance_document_number: data.insurance_document_number || null,
          insurance_provider: data.insurance_provider || null,
          insurance_type: data.insurance_type || null,
          insurance_valid_from: data.insurance_valid_from || null,
          insurance_valid_until: data.insurance_valid_until || null,
          insurance_status: data.insurance_status || "unknown",
          source: data.source === "transpordiamet_pdf" ? "transpordiamet_pdf" : "screenshot_ocr",
        }, scopeUserId);
        savedCount += 1;
        patchBatchItem(item.id, { status: "saved", progress: 100 });
      } catch (error) {
        const detail = error.response?.data?.detail;
        patchBatchItem(item.id, {
          status: "save_error",
          error: (typeof detail === "string" ? detail : detail?.message) || t("fleet_error_create"),
        });
      }
      processedCount += 1;
      setBatchFlow({
        active: true,
        phase: "save",
        percent: Math.round((processedCount / Math.max(1, candidates.length)) * 100),
        done: processedCount,
        total: candidates.length,
        current: item.name,
      });
    }

    setBatchSaving(false);
    setBatchFlow((flow) => ({ ...flow, active: false, percent: 100, done: processedCount }));
    if (savedCount) {
      setSuccess(modalText("fleet_batch_saved_count", { count: savedCount }));
      await load("");
    }
  };

  // Ready/review is derived from the CURRENT field values, not from a stale
  // OCR status. A row can start as "review", but as soon as plate + inspection
  // expiry are valid it becomes saveable immediately.
  const batchReadyCount = batchItems.filter(
    (item) => item.status !== "saved" && item.status !== "saving" && isBatchDataReady(item.data)
  ).length;
  const batchNeedsReviewCount = batchItems.filter((item) => {
    if (["saved", "saving", "processing", "queued"].includes(item.status)) return false;
    return !isBatchDataReady(item.data) || ["error", "save_error"].includes(item.status);
  }).length;
  const batchFailedCount = batchItems.filter((item) => ["error", "save_error"].includes(item.status)).length;

  const history = async (vehicle) => {
    setDrawer(vehicle);
    try {
      setTimeline((await A.timeline({ vehicle_id: vehicle.id })).data || []);
    } catch {
      setTimeline([]);
    }
  };

  const startInspectionUpdate = (vehicle) => {
    setModalError("");
    setInsuranceExpanded(Boolean(
      vehicle.insurance_provider ||
      vehicle.insurance_document_number ||
      vehicle.insurance_type ||
      vehicle.insurance_valid_from ||
      vehicle.insurance_valid_until ||
      (vehicle.insurance_status && vehicle.insurance_status !== "unknown")
    ));
    setEditInspection(vehicle);
    setNewInspectionDate(vehicle.inspection_date || "");
    setNewValidUntil(vehicle.inspection_valid_until || "");
    setNewInsuranceDocumentNumber(vehicle.insurance_document_number || "");
    setNewInsuranceProvider(vehicle.insurance_provider || "");
    setNewInsuranceType(vehicle.insurance_type || "");
    setNewInsuranceValidFrom(vehicle.insurance_valid_from || "");
    setNewInsuranceValidUntil(vehicle.insurance_valid_until || "");
    setNewInsuranceStatus(vehicle.insurance_status || "unknown");
    resetOcr();
  };

  const closeInspection = () => {
    if (saving || ocrLoading) return;
    setEditInspection(null);
    setInsuranceExpanded(false);
    setModalError("");
    resetOcr();
  };

  const saveInspection = async (event) => {
    event?.preventDefault();
    if (!editInspection || !newValidUntil) return;

    setSaving(true);
    setModalError("");
    try {
      await A.updateCompliance(editInspection.id, {
        inspection_valid_until: newValidUntil || null,
        inspection_date: newInspectionDate || null,
        insurance_document_number: newInsuranceDocumentNumber || null,
        insurance_provider: newInsuranceProvider || null,
        insurance_type: newInsuranceType || null,
        insurance_valid_from: newInsuranceValidFrom || null,
        insurance_valid_until: newInsuranceValidUntil || null,
        insurance_status: newInsuranceStatus || "unknown",
        source: ocrResult?.inspection_valid_until || ocrResult?.insurance_detected ? "screenshot_ocr" : "manual",
      });
      setEditInspection(null);
      setInsuranceExpanded(false);
      setModalError("");
      resetOcr();
      setSuccess(t("fleet_inspection_updated"));
      await load("");
    } catch (e) {
      setModalError(e.response?.data?.detail || t("fleet_error_update"));
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (date) =>
    date
      ? new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium" }).format(
          new Date(`${date}T12:00:00`)
        )
      : "—";

  const statusLabel = (value) =>
    t(`fleet_status_${value || "unknown"}`, { defaultValue: value || "—" });

  const openRegistry = () =>
    window.open(TRANSPORDIAMET_URL, "_blank", "noopener,noreferrer");

  const openRegistryCheck = (vehicle) => {
    setRegistryCheckVehicle(vehicle || null);
  };

  const copyAndOpenRegistry = async () => {
    if (!registryCheckVehicle) return;
    const preferred = registryCheckVehicle.registration_number || registryCheckVehicle.vin || "";
    if (preferred) {
      await copyIdentifier(preferred, `registry-check-${registryCheckVehicle.id}`);
    }
    openRegistry();
  };

  const updateColumnFilter = (key, value) => {
    setColumnFilters((current) => ({ ...current, [key]: value }));
    setPage(1);
  };

  const clearColumnFilters = () => {
    setColumnFilters({ status: "", vehicle: "", vin: "", inspection: "", insurance: "" });
    setPage(1);
  };

  const copyIdentifier = async (value, key) => {
    const text = String(value || "").trim();
    if (!text) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      setCopiedKey(key);
      window.setTimeout(() => setCopiedKey((current) => (current === key ? "" : current)), 1200);
    } catch (copyError) {
      console.error("Could not copy FleetOps identifier", copyError);
    }
  };

  const filteredVehicles = useMemo(() => {
    const normalized = Object.fromEntries(
      Object.entries(columnFilters).map(([key, value]) => [
        key,
        String(value || "").trim().toLocaleLowerCase(),
      ])
    );

    return vehicles.filter((vehicle) => {
      const vehicleText = [
        vehicle.registration_number,
        vehicle.make,
        vehicle.model,
        vehicle.year,
      ]
        .filter(Boolean)
        .join(" ")
        .toLocaleLowerCase();
      const vinText = String(vehicle.vin || "").toLocaleLowerCase();
      const inspectionText = [
        vehicle.inspection_valid_until,
        formatDate(vehicle.inspection_valid_until),
        vehicle.days_remaining,
        statusLabel(vehicle.status),
      ]
        .filter((value) => value !== null && value !== undefined)
        .join(" ")
        .toLocaleLowerCase();
      const insuranceText = [
        vehicle.insurance_provider,
        vehicle.insurance_document_number,
        vehicle.insurance_type,
        vehicle.insurance_valid_from,
        vehicle.insurance_valid_until,
        formatDate(vehicle.insurance_valid_until),
        t(`fleet_insurance_status_${vehicle.insurance_status || "unknown"}`),
        statusLabel(vehicle.insurance_state),
      ]
        .filter(Boolean)
        .join(" ")
        .toLocaleLowerCase();

      const overallState = overallFleetState(vehicle);
      return (
        (!normalized.status || overallState === normalized.status) &&
        (!normalized.vehicle || vehicleText.includes(normalized.vehicle)) &&
        (!normalized.vin || vinText.includes(normalized.vin)) &&
        (!normalized.inspection || inspectionText.includes(normalized.inspection)) &&
        (!normalized.insurance || insuranceText.includes(normalized.insurance))
      );
    }).sort((a, b) => {
      const [rankA, daysA] = fleetPriorityRank(a);
      const [rankB, daysB] = fleetPriorityRank(b);
      if (rankA !== rankB) return rankA - rankB;
      if (daysA !== daysB) return daysA - daysB;
      return String(a.registration_number || "").localeCompare(String(b.registration_number || ""), undefined, { numeric: true });
    });
  }, [vehicles, columnFilters, i18n.language, t]);

  const totalPages = Math.max(1, Math.ceil(filteredVehicles.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paginatedVehicles = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredVehicles.slice(start, start + pageSize);
  }, [filteredVehicles, currentPage, pageSize]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const paginationItems = useMemo(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, index) => index + 1);
    const items = [1];
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);
    if (start > 2) items.push("left-ellipsis");
    for (let number = start; number <= end; number += 1) items.push(number);
    if (end < totalPages - 1) items.push("right-ellipsis");
    items.push(totalPages);
    return items;
  }, [currentPage, totalPages]);

  const allPageVehiclesSelected = paginatedVehicles.length > 0 && paginatedVehicles.every((vehicle) => selectedVehicleIds.has(vehicle.id));

  const toggleVehicleSelected = (vehicleId) => {
    setSelectedVehicleIds((previous) => {
      const next = new Set(previous);
      if (next.has(vehicleId)) next.delete(vehicleId);
      else next.add(vehicleId);
      return next;
    });
  };

  const togglePageVehicleSelection = () => {
    setSelectedVehicleIds((previous) => {
      const next = new Set(previous);
      if (allPageVehiclesSelected) paginatedVehicles.forEach((vehicle) => next.delete(vehicle.id));
      else paginatedVehicles.forEach((vehicle) => next.add(vehicle.id));
      return next;
    });
  };

  const deleteSelectedVehicles = async () => {
    const ids = Array.from(selectedVehicleIds);
    if (!ids.length || deletingVehicles) return;
    if (!window.confirm(registryText("fleet_delete_selected_confirm", { count: ids.length }))) return;
    setDeletingVehicles(true);
    setError("");
    try {
      const response = await A.deleteVehicleBatch(ids);
      const deleted = Number(response?.data?.deleted || ids.length);
      setSelectedVehicleIds(new Set());
      setSuccess(registryText("fleet_selected_deleted", { count: deleted }));
      await load("");
    } catch (e) {
      setError(e.response?.data?.detail || registryText("fleet_error_delete_selected"));
    } finally {
      setDeletingVehicles(false);
    }
  };

  const deleteAllVehicles = async () => {
    if (!vehicles.length || deletingVehicles) return;
    const word = registryText("fleet_delete_all_word");
    const entered = window.prompt(registryText("fleet_delete_all_confirm", { word }));
    if (entered !== word) return;
    setDeletingVehicles(true);
    setError("");
    try {
      const response = await A.purgeAllVehicles(scopeUserId);
      const deleted = Number(response?.data?.deleted || 0);
      setSelectedVehicleIds(new Set());
      setSuccess(registryText("fleet_all_deleted", { count: deleted }));
      setPage(1);
      await load("");
    } catch (e) {
      setError(e.response?.data?.detail || registryText("fleet_error_delete_all"));
    } finally {
      setDeletingVehicles(false);
    }
  };

  const parseScreenshot = async (file, mode) => {
    if (!file) return;

    setOcrLoading(true);
    setOcrError("");
    setOcrProgress(0);
    setOcrFileName(file.name);
    setOcrResult(null);

    try {
      const result = await parseVehicleScreenshotInBrowser(
        file,
        setOcrProgress
      );
      setOcrResult(result);
      if (result.insurance_detected) setInsuranceExpanded(true);

      if (mode === "add") {
        // OCR fills only empty identity fields. It never silently overwrites
        // values the user has already verified.
        if (result.registration_number && !registrationNumber) {
          setRegistrationNumber(result.registration_number);
        }
        if (result.vin && !vin) {
          setVin(result.vin);
        }
        if (result.inspection_valid_until) setValidUntil(result.inspection_valid_until);
        if (result.insurance_document_number) setInsuranceDocumentNumber(result.insurance_document_number);
        if (result.insurance_provider) setInsuranceProvider(result.insurance_provider);
        if (result.insurance_type) setInsuranceType(result.insurance_type);
        if (result.insurance_valid_from) setInsuranceValidFrom(result.insurance_valid_from);
        if (result.insurance_valid_until) setInsuranceValidUntil(result.insurance_valid_until);
        if (result.insurance_status) setInsuranceStatus(result.insurance_status);
      } else {
        if (result.inspection_valid_until) setNewValidUntil(result.inspection_valid_until);
        if (result.insurance_document_number) setNewInsuranceDocumentNumber(result.insurance_document_number);
        if (result.insurance_provider) setNewInsuranceProvider(result.insurance_provider);
        if (result.insurance_type) setNewInsuranceType(result.insurance_type);
        if (result.insurance_valid_from) setNewInsuranceValidFrom(result.insurance_valid_from);
        if (result.insurance_valid_until) setNewInsuranceValidUntil(result.insurance_valid_until);
        if (result.insurance_status) setNewInsuranceStatus(result.insurance_status);
      }

      if (!result.inspection_valid_until && !result.insurance_detected) {
        setOcrError(t("fleet_screenshot_not_found"));
      }
    } catch (e) {
      console.error("FleetOps screenshot OCR failed", e);
      setOcrError(t("fleet_ocr_error"));
    } finally {
      setOcrLoading(false);
    }
  };

  const OcrBox = ({ mode }) => (
    <div className={s.ocrBox}>
      <div className={s.ocrHead}>
        <div>
          <strong>{t("fleet_screenshot_title")}</strong>
          <small>{t("fleet_screenshot_hint")}</small>
        </div>
        <label className={`${s.btnSecondary} ${s.fileButton}`}>
          {ocrLoading
            ? t("fleet_screenshot_reading")
            : t("fleet_choose_screenshot")}
          <input
            type="file"
            accept="application/pdf,.pdf,image/png,image/jpeg"
            disabled={ocrLoading || saving}
            onChange={(event) =>
              parseScreenshot(event.target.files?.[0], mode)
            }
          />
        </label>
      </div>

      {ocrFileName && <div className={s.ocrFile}>📎 {ocrFileName}</div>}

      {ocrLoading && (
        <div className={s.ocrProgress}>
          <div className={s.spinner} />
          <span>
            {t("fleet_screenshot_analyzing")}
            {ocrProgress > 0 ? ` · ${ocrProgress}%` : ""}
          </span>
        </div>
      )}

      {ocrError && (
        <div className={s.validationHint} style={{ marginTop: 10 }}>
          ⚠ {ocrError}
        </div>
      )}

      {ocrResult && (
        <div
          className={`${s.ocrResult} ${
            s[`confidence_${ocrResult.confidence}`] || ""
          }`}
        >
          <div className={s.ocrResultTop}>
            <strong>
              {ocrResult.inspection_valid_until
                ? t("fleet_screenshot_found")
                : t("fleet_screenshot_not_found")}
            </strong>
            <span>
              {t(`fleet_confidence_${ocrResult.confidence}`, {
                defaultValue: ocrResult.confidence,
              })}
            </span>
          </div>

          <div className={s.ocrDetectedGrid}>
            <div>
              <span>{t("fleet_registration")}</span>
              <b>{ocrResult.registration_number || "—"}</b>
            </div>
            <div>
              <span>{t("fleet_vin")}</span>
              <b className={s.mono}>{ocrResult.vin || "—"}</b>
            </div>
            <div>
              <span>{t("fleet_valid_until")}</span>
              <b>
                {ocrResult.inspection_valid_until
                  ? formatDate(ocrResult.inspection_valid_until)
                  : "—"}
              </b>
              {ocrResult.date_precision === "month" && (
                <small>{t("fleet_month_precision_note")}</small>
              )}
            </div>
          </div>

          {ocrResult.insurance_detected && (
            <div className={s.ocrInsuranceBlock}>
              <div className={s.ocrInsuranceTitle}>{t("fleet_insurance_detected")}</div>
              <div className={s.ocrInsuranceGrid}>
                <div><span>{t("fleet_insurance_provider")}</span><b>{ocrResult.insurance_provider || "—"}</b></div>
                <div><span>{t("fleet_insurance_document")}</span><b className={s.mono}>{ocrResult.insurance_document_number || "—"}</b></div>
                <div><span>{t("fleet_insurance_type")}</span><b>{ocrResult.insurance_type || "—"}</b></div>
                <div><span>{t("fleet_insurance_valid_from")}</span><b>{ocrResult.insurance_valid_from ? formatDate(ocrResult.insurance_valid_from) : "—"}</b></div>
                <div><span>{t("fleet_insurance_valid_until")}</span><b>{ocrResult.insurance_valid_until ? formatDate(ocrResult.insurance_valid_until) : t(`fleet_insurance_status_${ocrResult.insurance_status || "unknown"}`)}</b></div>
              </div>
            </div>
          )}

          <small className={s.ocrConfirm}>
            {t("fleet_ocr_confirm_note")}
          </small>
        </div>
      )}
    </div>
  );

  const BatchUploadBox = () => (
    <section className={`${s.cleanSection} ${s.batchImportSection}`}>
      {batchFlow.active && (
        <div className={s.batchProcessOverlay} role="status" aria-live="polite">
          <div className={s.batchProcessCard}>
            <div className={s.batchProcessSpinner}>
              <span />
              <strong>{Math.max(0, Math.min(100, batchFlow.percent || 0))}%</strong>
            </div>
            <h3>{batchFlow.phase === "save" ? modalText("fleet_batch_save_progress_title") : modalText("fleet_batch_progress_title")}</h3>
            <p>{batchFlow.current ? modalText("fleet_batch_progress_file", { name: batchFlow.current }) : ""}</p>
            <div className={s.batchProcessTrack}><span style={{ width: `${Math.max(2, Math.min(100, batchFlow.percent || 0))}%` }} /></div>
            <div className={s.batchProcessSteps}>
              {["upload", "parse", "save"].map((phase) => {
                const order = { upload: 0, parse: 1, save: 2 };
                const current = order[batchFlow.phase] ?? 0;
                const index = order[phase];
                const active = index === current;
                const done = index < current || (batchFlow.phase === "save" && phase !== "save");
                return (
                  <div key={phase} className={`${s.batchProcessStep} ${active ? s.batchProcessStepActive : ""} ${done ? s.batchProcessStepDone : ""}`}>
                    <span>{done ? "✓" : index + 1}</span>
                    <b>{modalText(`fleet_batch_stage_${phase}`)}</b>
                  </div>
                );
              })}
            </div>
            <small>
              {batchFlow.phase === "save"
                ? modalText("fleet_batch_save_progress_summary", { done: batchFlow.done, total: batchFlow.total })
                : modalText("fleet_batch_progress_summary", { done: batchFlow.done, total: batchFlow.total })}
            </small>
          </div>
        </div>
      )}
      <div className={s.batchSectionHead}>
        <div>
          <div className={s.batchTitleLine}>
            <span className={s.batchStep}>01</span>
            <div>
              <h4>{modalText("fleet_batch_title")}</h4>
              <p>{modalText("fleet_batch_hint")}</p>
            </div>
          </div>
        </div>
        <button type="button" className={s.registryCompact} onClick={openRegistry}>
          <span>↗</span>
          {t("fleet_open_registry")}
        </button>
      </div>

      <label
        className={`${s.batchDropzone} ${batchDragActive ? s.batchDropzoneActive : ""} ${batchProcessing ? s.batchDropzoneBusy : ""}`}
        onDragEnter={(event) => { event.preventDefault(); setBatchDragActive(true); }}
        onDragOver={(event) => { event.preventDefault(); setBatchDragActive(true); }}
        onDragLeave={(event) => {
          event.preventDefault();
          if (event.currentTarget === event.target) setBatchDragActive(false);
        }}
        onDrop={(event) => {
          event.preventDefault();
          setBatchDragActive(false);
          processBatchFiles(event.dataTransfer.files);
        }}
      >
        <input
          type="file"
          accept="application/pdf,.pdf,image/png,image/jpeg"
          multiple
          disabled={batchProcessing || batchSaving || saving}
          onChange={(event) => {
            processBatchFiles(event.target.files);
            event.target.value = "";
          }}
        />
        <span className={s.batchDropIcon}>{batchProcessing ? "◌" : "⇧"}</span>
        <div className={s.batchDropCopy}>
          <strong>{batchProcessing ? modalText("fleet_batch_processing") : modalText("fleet_batch_drop_title")}</strong>
          <span>{modalText("fleet_batch_drop_subtitle")}</span>
        </div>
        <div className={s.batchDropMeta}>
          <span>PDF / PNG / JPG</span>
          <span>{modalText("fleet_batch_limit")}</span>
        </div>
      </label>

      {batchItems.length > 0 && (
        <div className={s.batchQueue}>
          <div className={s.batchQueueHead}>
            <div>
              <strong>{modalText("fleet_batch_queue_title", { count: batchItems.length })}</strong>
              <span>
                {modalText("fleet_batch_ready_summary", {
                  ready: batchReadyCount,
                  review: batchNeedsReviewCount,
                })}
              </span>
            </div>
            <button
              type="button"
              className={s.batchClear}
              onClick={clearBatch}
              disabled={batchProcessing || batchSaving}
            >
              {modalText("fleet_batch_clear")}
            </button>
          </div>

          <div className={s.batchRows}>
            {batchItems.map((item, index) => {
              const data = item.data || {};
              const locked = ["processing", "saving", "saved"].includes(item.status);
              const derivedStatus = ["queued", "processing", "saving", "saved", "error", "save_error"].includes(item.status)
                ? item.status
                : (isBatchDataReady(data) ? "ready" : "review");
              return (
                <div className={`${s.batchRow} ${s[`batch_${derivedStatus}`] || ""}`} key={item.id}>
                  <div className={s.batchRowTop}>
                    <div className={s.batchFileIdentity}>
                      <span className={s.batchIndex}>{index + 1}</span>
                      <div>
                        <strong title={item.name}>{item.name}</strong>
                        <small>{item.fileKind === "pdf" ? "PDF" : "IMG"} · {(item.size / 1024 / 1024).toFixed(1)} MB</small>
                      </div>
                    </div>
                    <div className={s.batchRowActions}>
                      <span className={s.batchStatus}>{modalText(`fleet_batch_status_${derivedStatus}`)}</span>
                      <button
                        type="button"
                        className={s.batchRemove}
                        onClick={() => removeBatchItem(item.id)}
                        disabled={batchProcessing || batchSaving || item.status === "saving"}
                        aria-label={t("remove")}
                      >
                        ×
                      </button>
                    </div>
                  </div>

                  {item.status === "processing" && (
                    <div className={s.batchProgressTrack}>
                      <span style={{ width: `${Math.max(4, item.progress || 0)}%` }} />
                    </div>
                  )}

                  {!['queued', 'processing', 'error'].includes(item.status) && (
                    <>
                      <div className={s.batchFields}>
                        <div className={s.batchField}>
                          <label>{t('fleet_registration')}</label>
                          <input
                            value={String(data.registration_number || '')}
                            onChange={(event) => updateBatchField(item.id, 'registration_number', event.target.value)}
                            placeholder={modalText('fleet_registration_enter_placeholder')}
                            autoComplete="off"
                            disabled={locked}
                          />
                        </div>
                        <div className={s.batchField}>
                          <label>{t('fleet_vin')}</label>
                          <input
                            className={s.monoInput}
                            value={String(data.vin || '')}
                            onChange={(event) => updateBatchField(item.id, 'vin', event.target.value)}
                            placeholder={modalText('fleet_vin_enter_placeholder')}
                            autoComplete="off"
                            maxLength={17}
                            disabled={locked}
                          />
                        </div>
                        <div className={s.batchField}>
                          <label>{t('fleet_valid_until')}</label>
                          <input
                            type="date"
                            value={data.inspection_valid_until || ''}
                            onChange={(event) => updateBatchField(item.id, 'inspection_valid_until', event.target.value)}
                            disabled={locked}
                          />
                        </div>
                      </div>

                      {(data.make || data.model) && (
                        <div className={s.batchInsuranceLine}>
                          <span>{t('fleet_vehicle')}</span>
                          <b>{[data.make, data.model, data.year].filter(Boolean).join(' · ')}</b>
                        </div>
                      )}

                      {data.insurance_detected && (
                        <div className={s.batchInsuranceLine}>
                          <span>◇ {t('fleet_insurance_title')}</span>
                          <b>
                            {[data.insurance_provider, data.insurance_document_number, data.insurance_type]
                              .filter(Boolean)
                              .join(' · ') || t(`fleet_insurance_status_${data.insurance_status || 'unknown'}`)}
                          </b>
                        </div>
                      )}
                    </>
                  )}

                  {derivedStatus === "review" && (() => {
                    const validation = getBatchValidation(data);
                    return (
                      <div className={s.batchReviewHint}>
                        ⚠ {modalText("fleet_batch_review_hint")}
                        {!validation.hasRegistration && (
                          <span> {modalText("fleet_batch_missing_registration")}</span>
                        )}
                        {!validation.hasInspectionDeadline && (
                          <span> {modalText("fleet_batch_missing_deadline")}</span>
                        )}
                      </div>
                    );
                  })()}
                  {item.sourceSaved && (
                    <div className={s.batchSourceSaved}>✓ {modalText("fleet_batch_source_saved")}{item.sourceFileId ? ` · ${item.sourceFileId.slice(0, 8)}` : ""}</div>
                  )}
                  {item.error && <div className={s.batchError}>⚠ {item.error}</div>}
                </div>
              );
            })}
          </div>

          {batchFailedCount > 0 && (
            <div className={s.batchFailurePanel}>
              <div>
                <strong>{modalText("fleet_batch_failed_title", { count: batchFailedCount })}</strong>
                <span>{modalText("fleet_batch_failed_hint")}</span>
              </div>
              <div className={s.batchFailureActions}>
                <button type="button" className={s.btnSecondary} onClick={downloadBatchErrorReport} disabled={batchProcessing || batchSaving}>
                  ↓ {modalText("fleet_batch_download_report")}
                </button>
                <button type="button" className={s.btn} onClick={retryFailedBatchFiles} disabled={batchProcessing || batchSaving}>
                  ↻ {modalText("fleet_batch_retry_failed")}
                </button>
              </div>
            </div>
          )}

          <div className={s.batchQueueFooter}>
            <div>
              <strong>{modalText("fleet_batch_save_title")}</strong>
              <span>{modalText("fleet_batch_save_hint")}</span>
            </div>
            <button
              type="button"
              className={s.btn}
              onClick={saveBatchVehicles}
              disabled={!batchReadyCount || batchProcessing || batchSaving}
            >
              {batchSaving
                ? t("saving")
                : modalText("fleet_batch_save_button", { count: batchReadyCount })}
            </button>
          </div>
        </div>
      )}
    </section>
  );

  return (
    <div className={s.page}>
      <div className={s.header}>
        <div>
          <div className={s.eyebrow}>{t("fleet_eyebrow")}</div>
          <h1 className={s.title}>{t("fleet_control")}</h1>
          <div className={s.sub}>{t("fleet_subtitle")}</div>
        </div>
        <div className={s.headerActions}>
          <button className={s.btnSecondary} onClick={() => load("")}>
            ↻ {t("refresh")}
          </button>
          <button className={s.btn} onClick={() => { setModalError(""); setShowAdd(true); }}>
            ＋ {t("fleet_add_vehicle")}
          </button>
        </div>
      </div>

      {error && <div className={s.alert}>{error}</div>}
      {success && (
        <div className={s.success} onClick={() => setSuccess("")}>
          {success}
        </div>
      )}

      <div className={s.freeModeBanner}>
        <div className={s.freeModeIcon}>✓</div>
        <div>
          <strong>{t("fleet_free_mode_title")}</strong>
          <span>{t("fleet_free_mode_text")}</span>
        </div>
        <button className={s.btnGhost} onClick={openRegistry}>
          ↗ {t("fleet_open_registry")}
        </button>
      </div>

      <section className={s.telegramPanel}>
        <div className={s.telegramPanelHead}>
          <div className={s.telegramIcon}>✈</div>
          <div className={s.telegramHeading}>
            <div className={s.telegramTitleRow}>
              <strong>{telegramText("fleet_telegram_title")}</strong>
              <span
                className={`${s.telegramStatusBadge} ${
                  telegramStatus?.enabled && telegramStatus?.bot_configured && telegramStatus?.recipient_configured
                    ? s.telegramConnected
                    : s.telegramDisconnected
                }`}
              >
                {telegramLoading
                  ? telegramText("fleet_telegram_checking")
                  : telegramStatus?.enabled && telegramStatus?.bot_configured && telegramStatus?.recipient_configured
                    ? telegramText("fleet_telegram_connected")
                    : telegramText("fleet_telegram_not_connected")}
              </span>
            </div>
            <span>{telegramText("fleet_telegram_subtitle")}</span>
          </div>
          <div className={s.telegramHeaderTools}>
            <button
              type="button"
              className={s.telegramScheduleButton}
              onClick={openScheduleSettings}
              aria-haspopup="dialog"
              aria-expanded={scheduleOpen}
              title={telegramText("fleet_telegram_schedule_settings")}
            >
              <span>{telegramText("fleet_telegram_schedule")}</span>
              <strong>{telegramForm.yellow_time || "12:00"} · {telegramForm.timezone || "Europe/Tallinn"}</strong>
              <i aria-hidden="true">›</i>
            </button>

            <div className={s.telegramRulesInfoWrap} ref={rulesInfoRef}>
              <button
                type="button"
                className={s.telegramRulesInfoButton}
                aria-label={telegramText("fleet_telegram_rules_title")}
                aria-expanded={rulesInfoOpen}
                onClick={() => setRulesInfoOpen((value) => !value)}
                title={telegramText("fleet_telegram_rules_title")}
              >
                i
              </button>
              <div
                className={`${s.telegramRulesPopover} ${rulesInfoOpen ? s.telegramRulesPopoverOpen : ""}`}
                role="dialog"
                aria-label={telegramText("fleet_telegram_rules_title")}
              >
                <div className={s.telegramRulesPopoverHead}>
                  <strong>{telegramText("fleet_telegram_rules_title")}</strong>
                  <button
                    type="button"
                    onClick={() => setRulesInfoOpen(false)}
                    aria-label={telegramText("fleet_telegram_schedule_cancel")}
                  >
                    ×
                  </button>
                </div>
                <div className={s.telegramInfoRule}>
                  <span className={`${s.scheduleModeDot} ${s.scheduleModeGreen}`} />
                  <div>
                    <strong>{telegramText("fleet_telegram_schedule_green")}</strong>
                    <small>{telegramText("fleet_telegram_green_rule", { time: telegramForm.green_time || "12:00" })}</small>
                  </div>
                </div>
                <div className={s.telegramInfoRule}>
                  <span className={`${s.scheduleModeDot} ${s.scheduleModeYellow}`} />
                  <div>
                    <strong>{telegramText("fleet_telegram_schedule_yellow")}</strong>
                    <small>{telegramText("fleet_telegram_yellow_rule", { time: telegramForm.yellow_time || "12:00" })}</small>
                  </div>
                </div>
                <div className={s.telegramInfoRule}>
                  <span className={`${s.scheduleModeDot} ${s.scheduleModeRed}`} />
                  <div>
                    <strong>{telegramText("fleet_telegram_schedule_red")}</strong>
                    <small>{telegramText("fleet_telegram_red_rule", {
                      count: telegramForm.red_times_per_day || 1,
                      start: telegramForm.red_window_start || "12:00",
                      end: telegramForm.red_window_end || "18:00",
                    })}</small>
                  </div>
                </div>
                <p>{telegramText("fleet_telegram_until_action")}</p>
              </div>
            </div>
          </div>
        </div>

        <div className={s.telegramGrid}>
          <label className={s.telegramField}>
            <span>{telegramText("fleet_telegram_chat_label")}</span>
            <input
              value={telegramForm.chat_id}
              onChange={(e) => setTelegramForm((prev) => ({ ...prev, chat_id: e.target.value }))}
              placeholder={telegramText("fleet_telegram_chat_placeholder")}
            />
            <small>{telegramText("fleet_telegram_chat_hint")}</small>
          </label>

          <div className={s.telegramSubscriptions}>
            <span className={s.telegramSubscriptionsTitle}>{telegramText("fleet_telegram_subscriptions")}</span>
            <label className={s.telegramToggle}>
              <input
                type="checkbox"
                checked={telegramForm.inspection_enabled}
                onChange={(e) => setTelegramForm((prev) => ({ ...prev, inspection_enabled: e.target.checked }))}
              />
              <span>🔧 {telegramText("fleet_telegram_inspection")}</span>
            </label>
            <label className={s.telegramToggle}>
              <input
                type="checkbox"
                checked={telegramForm.insurance_enabled}
                onChange={(e) => setTelegramForm((prev) => ({ ...prev, insurance_enabled: e.target.checked }))}
              />
              <span>🛡️ {telegramText("fleet_telegram_insurance")}</span>
            </label>
          </div>

          <div className={s.telegramActions}>
            <button
              className={s.btnSecondary}
              type="button"
              disabled={telegramSaving || telegramLoading}
              onClick={saveTelegramPreferences}
            >
              {telegramSaving ? telegramText("fleet_telegram_sending") : telegramText("fleet_telegram_save")}
            </button>
            <button
              className={s.telegramTestBtn}
              type="button"
              disabled={Boolean(telegramTesting) || !canTestTelegram}
              onClick={() => testTelegram("inspection")}
            >
              {telegramTesting === "inspection" ? telegramText("fleet_telegram_sending") : telegramText("fleet_telegram_test_inspection")}
            </button>
            <button
              className={s.telegramTestBtnDanger}
              type="button"
              disabled={Boolean(telegramTesting) || !canTestTelegram}
              onClick={() => testTelegram("insurance")}
            >
              {telegramTesting === "insurance" ? telegramText("fleet_telegram_sending") : telegramText("fleet_telegram_test_insurance")}
            </button>
          </div>
        </div>

        {!telegramStatus?.bot_configured && !telegramLoading && (
          <div className={s.telegramWarning}>{telegramText("fleet_telegram_bot_not_configured")}</div>
        )}
        {telegramStatus?.bot_configured && telegramForm.chat_id && !telegramStatus?.enabled && !telegramLoading && (
          <div className={s.telegramWarning}>{telegramText("fleet_telegram_bot_disabled")}</div>
        )}
        {telegramFeedback && (
          <div className={telegramFeedback.type === "success" ? s.telegramFeedbackOk : s.telegramFeedbackError}>
            {telegramFeedback.text}
          </div>
        )}
      </section>

      {scheduleOpen && (
        <div
          className={s.scheduleModalBackdrop}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !telegramSaving) setScheduleOpen(false);
          }}
        >
          <div
            className={s.scheduleModal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="fleetops-schedule-title"
          >
            <div className={s.scheduleModalHead}>
              <div>
                <div className={s.scheduleModalEyebrow}>{telegramText("fleet_telegram_schedule")}</div>
                <h3 id="fleetops-schedule-title">{telegramText("fleet_telegram_schedule_title")}</h3>
                <p>{telegramText("fleet_telegram_schedule_subtitle")}</p>
              </div>
              <button
                type="button"
                className={s.scheduleModalClose}
                onClick={() => setScheduleOpen(false)}
                disabled={telegramSaving}
                aria-label={telegramText("fleet_telegram_schedule_cancel")}
              >
                ×
              </button>
            </div>

            <div className={s.scheduleModalBody}>
              <section className={`${s.scheduleSettingsSection} ${s.scheduleTimezoneSection}`}>
                <div className={s.scheduleSectionTitle}>
                  <span className={`${s.scheduleModeDot} ${s.scheduleModeNeutral}`} />
                  <div>
                    <strong>{telegramText("fleet_telegram_schedule_general")}</strong>
                  </div>
                </div>
                <div className={s.scheduleField}>
                  <span>{telegramText("fleet_telegram_timezone")}</span>
                  <TimeZonePicker
                    value={scheduleDraft.timezone}
                    options={timeZoneOptions}
                    onChange={(timezone) => setScheduleDraft((prev) => ({ ...prev, timezone }))}
                    now={new Date(clockTick)}
                    locale={activeLanguage}
                    label={telegramText("fleet_telegram_timezone")}
                    searchPlaceholder={telegramText("fleet_telegram_timezone_search_placeholder")}
                    emptyText={telegramText("fleet_telegram_timezone_no_results")}
                  />
                  <small className={s.scheduleFieldHelp}>{telegramText("fleet_telegram_timezone_search_hint")}</small>
                </div>
                <div className={s.scheduleClockGrid}>
                  <div className={s.scheduleClockCard}>
                    <span>{telegramText("fleet_telegram_timezone_selected_now")}</span>
                    <strong>{selectedZoneNow}</strong>
                    <small>{scheduleDraft.timezone || "Europe/Tallinn"}</small>
                  </div>
                  <div className={s.scheduleClockCard}>
                    <span>{telegramText("fleet_telegram_timezone_local_now")}</span>
                    <strong>{localZoneNow}</strong>
                    <small>{localTimeZone}</small>
                  </div>
                </div>
              </section>

              <section className={`${s.scheduleSettingsSection} ${s.scheduleGreenSection}`}>
                <div className={s.scheduleSectionTitle}>
                  <span className={`${s.scheduleModeDot} ${s.scheduleModeGreen}`} />
                  <div>
                    <strong>{telegramText("fleet_telegram_schedule_green")}</strong>
                    <small>{telegramText("fleet_telegram_schedule_green_hint")}</small>
                  </div>
                </div>
                <label className={s.scheduleField}>
                  <span>{telegramText("fleet_telegram_green_time")}</span>
                  <input
                    type="time"
                    value={scheduleDraft.green_time}
                    onChange={(e) => setScheduleDraft((prev) => ({ ...prev, green_time: e.target.value }))}
                  />
                  <small className={s.scheduleTimePair}>
                    {telegramText("fleet_telegram_time_pair", {
                      zone: scheduleTimePreview(scheduleDraft.green_time).zone,
                      local: scheduleTimePreview(scheduleDraft.green_time).local,
                    })}
                  </small>
                </label>
              </section>

              <section className={`${s.scheduleSettingsSection} ${s.scheduleYellowSection}`}>
                <div className={s.scheduleSectionTitle}>
                  <span className={`${s.scheduleModeDot} ${s.scheduleModeYellow}`} />
                  <div>
                    <strong>{telegramText("fleet_telegram_schedule_yellow")}</strong>
                    <small>{telegramText("fleet_telegram_schedule_yellow_hint")}</small>
                  </div>
                </div>
                <label className={s.scheduleField}>
                  <span>{telegramText("fleet_telegram_yellow_time")}</span>
                  <input
                    type="time"
                    value={scheduleDraft.yellow_time}
                    onChange={(e) => setScheduleDraft((prev) => ({ ...prev, yellow_time: e.target.value }))}
                  />
                  <small className={s.scheduleTimePair}>
                    {telegramText("fleet_telegram_time_pair", {
                      zone: scheduleTimePreview(scheduleDraft.yellow_time).zone,
                      local: scheduleTimePreview(scheduleDraft.yellow_time).local,
                    })}
                  </small>
                </label>
              </section>

              <section className={`${s.scheduleSettingsSection} ${s.scheduleRedSection}`}>
                <div className={s.scheduleSectionTitle}>
                  <span className={`${s.scheduleModeDot} ${s.scheduleModeRed}`} />
                  <div>
                    <strong>{telegramText("fleet_telegram_schedule_red")}</strong>
                    <small>{telegramText("fleet_telegram_schedule_red_hint")}</small>
                  </div>
                </div>
                <div className={s.scheduleRedGrid}>
                  <label className={s.scheduleField}>
                    <span>{telegramText("fleet_telegram_red_start")}</span>
                    <input
                      type="time"
                      value={scheduleDraft.red_window_start}
                      onChange={(e) => setScheduleDraft((prev) => ({ ...prev, red_window_start: e.target.value }))}
                    />
                    <small className={s.scheduleTimePair}>{telegramText("fleet_telegram_local_time_short", { time: scheduleTimePreview(scheduleDraft.red_window_start).local })}</small>
                  </label>
                  <label className={s.scheduleField}>
                    <span>{telegramText("fleet_telegram_red_end")}</span>
                    <input
                      type="time"
                      value={scheduleDraft.red_window_end}
                      onChange={(e) => setScheduleDraft((prev) => ({ ...prev, red_window_end: e.target.value }))}
                    />
                    <small className={s.scheduleTimePair}>{telegramText("fleet_telegram_local_time_short", { time: scheduleTimePreview(scheduleDraft.red_window_end).local })}</small>
                  </label>
                  <label className={s.scheduleField}>
                    <span>{telegramText("fleet_telegram_red_count")}</span>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      step="1"
                      value={scheduleDraft.red_times_per_day}
                      onChange={(e) => setScheduleDraft((prev) => ({
                        ...prev,
                        red_times_per_day: Math.max(1, Math.min(100, Number(e.target.value || 1))),
                      }))}
                    />
                    <small className={`${s.scheduleTimePair} ${s.scheduleTimePairPlaceholder}`} aria-hidden="true">&nbsp;</small>
                  </label>
                </div>
                <div className={s.scheduleIntervalPreview}>
                  {Number(scheduleDraft.red_times_per_day || 1) > 1 && reminderIntervalLabel
                    ? telegramText("fleet_telegram_interval", { interval: reminderIntervalLabel })
                    : telegramText("fleet_telegram_interval_once", { time: scheduleDraft.red_window_start || "12:00" })}
                </div>
              </section>

              {telegramFeedback?.type === "error" && (
                <div className={s.scheduleModalError}>{telegramFeedback.text}</div>
              )}
            </div>

            <div className={s.scheduleModalFooter}>
              <button
                type="button"
                className={s.btnSecondary}
                onClick={() => setScheduleOpen(false)}
                disabled={telegramSaving}
              >
                {telegramText("fleet_telegram_schedule_cancel")}
              </button>
              <button
                type="button"
                className={s.btn}
                onClick={saveSchedulePreferences}
                disabled={telegramSaving}
              >
                {telegramSaving ? telegramText("fleet_telegram_sending") : telegramText("fleet_telegram_schedule_save")}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={s.cards}>
        <div className={s.card}>
          <div className={s.cardLabel}>{t("fleet_total")}</div>
          <div className={s.cardValue}>{stats.total}</div>
          <div className={s.cardHint}>{t("fleet_total_hint")}</div>
        </div>
        <div className={s.card}>
          <div className={s.cardLabel}>{t("fleet_due_30")}</div>
          <div className={s.cardValue}>{stats.due}</div>
          <div className={s.cardHint}>{t("fleet_due_hint")}</div>
        </div>
        <div className={`${s.card} ${s.kpiDanger}`}>
          <div className={s.cardLabel}>{t("fleet_critical")}</div>
          <div className={s.cardValue}>{stats.critical}</div>
          <div className={s.cardHint}>{t("fleet_critical_hint")}</div>
        </div>
        <div className={s.card}>
          <div className={s.cardLabel}>{t("fleet_healthy")}</div>
          <div className={s.cardValue}>{stats.ok}</div>
          <div className={s.cardHint}>{t("fleet_healthy_hint")}</div>
        </div>
      </div>

      <div className={s.panel}>
        <div className={s.panelHead}>
          <div>
            <h2 className={s.panelTitle}>{t("fleet_registry")}</h2>
            <div className={s.panelSub}>{t("fleet_registry_sub")}</div>
          </div>
          <div className={s.fleetRegistryHeadTools}>
            <FleetScopeUserPicker value={scopeUserId} onChange={changeScopeUser} t={t} />
            <div className={s.invoiceRegistryActions}>
            {selectedVehicleIds.size > 0 && (
              <>
                <span className={s.invoiceSelectedCount}>
                  {registryText("fleet_selected_count", { count: selectedVehicleIds.size })}
                </span>
                <button type="button" className={s.btnDangerSoft} onClick={deleteSelectedVehicles} disabled={deletingVehicles}>
                  🗑 {registryText("fleet_delete_selected")}
                </button>
              </>
            )}
            <button type="button" className={s.btnDangerGhost} onClick={deleteAllVehicles} disabled={deletingVehicles || !vehicles.length}>
              🗑 {registryText("fleet_delete_all")}
            </button>
            </div>
          </div>
        </div>
        <div className={s.registryToolbar}>
          <div className={s.registryToolbarSummary}>
            <strong>
              {registryText("fleet_results_count", {
                count: filteredVehicles.length,
                total: vehicles.length,
              })}
            </strong>
            <span>{registryText("fleet_filter_hint")}</span>
          </div>
          <button
            type="button"
            className={s.filterResetButton}
            onClick={clearColumnFilters}
            disabled={!Object.values(columnFilters).some(Boolean)}
          >
            {registryText("fleet_filter_clear_all")}
          </button>
        </div>

        <div className={s.registryFilters}>
          <label className={s.registryFilterField}>
            <span>{registryText("fleet_filter_status_label")}</span>
            <select
              value={columnFilters.status}
              onChange={(event) => updateColumnFilter("status", event.target.value)}
            >
              <option value="">{registryText("fleet_filter_status_all")}</option>
              <option value="green">{registryText("fleet_overall_green")}</option>
              <option value="yellow">{registryText("fleet_overall_yellow")}</option>
              <option value="overdue">{registryText("fleet_overall_overdue")}</option>
              <option value="red">{registryText("fleet_overall_red")}</option>
              <option value="unknown">{registryText("fleet_overall_unknown")}</option>
            </select>
          </label>
          {[
            ["vehicle", "fleet_filter_vehicle_label", "fleet_filter_vehicle"],
            ["vin", "fleet_filter_vin_label", "fleet_filter_vin"],
            ["inspection", "fleet_filter_inspection_label", "fleet_filter_inspection"],
            ["insurance", "fleet_filter_insurance_label", "fleet_filter_insurance"],
          ].map(([key, labelKey, placeholderKey]) => (
            <label className={s.registryFilterField} key={key}>
              <span>{registryText(labelKey)}</span>
              <div className={s.registryFilterInput}>
                <span className={s.registryFilterIcon}>⌕</span>
                <input
                  type="search"
                  value={columnFilters[key]}
                  onChange={(event) => updateColumnFilter(key, event.target.value)}
                  placeholder={registryText(placeholderKey)}
                  aria-label={registryText(labelKey)}
                />
              </div>
            </label>
          ))}
        </div>

        {loading ? (
          <div className={s.loading}>
            <div className={s.spinner} />
            {t("loading")}
          </div>
        ) : vehicles.length ? (
          <>
            <div className={`${s.tableWrap} ${s.registryTableWrap}`}>
              <table className={`${s.table} ${s.complianceTable} ${s.registryTableV2}`}>
                <thead>
                  <tr>
                    <th className={s.registrySelectCol}>
                      <input
                        type="checkbox"
                        aria-label={registryText("fleet_select")}
                        checked={allPageVehiclesSelected}
                        onChange={togglePageVehicleSelection}
                        disabled={!paginatedVehicles.length || deletingVehicles}
                      />
                    </th>
                    <th>{registryText("fleet_column_status")}</th>
                    <th>{registryText("fleet_column_vehicle")}</th>
                    <th>{registryText("fleet_column_vin")}</th>
                    <th>{registryText("fleet_column_inspection")}</th>
                    <th>{registryText("fleet_column_insurance")}</th>
                    <th>{registryText("fleet_column_actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedVehicles.map((vehicle) => {
                    const plateCopyKey = `plate-${vehicle.id}`;
                    const vinCopyKey = `vin-${vehicle.id}`;
                    const vehicleDescription = [vehicle.make, vehicle.model, vehicle.year]
                      .filter(Boolean)
                      .join(" · ");
                    const overallState = overallFleetState(vehicle);
                    const overdue = overallState === "overdue";
                    const overallLabel = registryText(`fleet_overall_${overallState}`);
                    const insuranceStatusText = vehicle.insurance_days_remaining !== null && vehicle.insurance_days_remaining !== undefined
                      ? statusLabel(vehicle.insurance_state)
                      : t(`fleet_insurance_status_${vehicle.insurance_status || "unknown"}`, {
                          defaultValue: vehicle.insurance_status || registryText("fleet_overall_unknown"),
                        });
                    const inspectionDays = Number(vehicle.days_remaining);
                    const inspectionDaysText = Number.isFinite(inspectionDays)
                      ? inspectionDays >= 0
                        ? registryText("fleet_days_remaining_full", { count: inspectionDays })
                        : registryText("fleet_days_overdue_full", { count: Math.abs(inspectionDays) })
                      : "—";
                    return (
                      <tr
                        key={vehicle.id}
                        className={`${s.registryVehicleRow} ${overdue ? s.registryOverdueRow : ""} ${selectedVehicleIds.has(vehicle.id) ? s.registryVehicleSelected : ""}`}
                      >
                        <td className={s.registrySelectCell} data-label={registryText("fleet_select")}>
                          <input
                            type="checkbox"
                            aria-label={`${registryText("fleet_select")} ${vehicle.registration_number || vehicle.id}`}
                            checked={selectedVehicleIds.has(vehicle.id)}
                            onChange={() => toggleVehicleSelected(vehicle.id)}
                            disabled={deletingVehicles}
                          />
                        </td>
                        <td className={s.registryStatusCellTd} data-label={registryText("fleet_column_status")}>
                          <div className={s.registryStatusCell}>
                            <span className={`${s.status} ${s.registryPrimaryStatus} ${statusClass(overallState)}`}>
                              {overdue ? (
                                <span className={s.registryOverdueFlagInline} aria-hidden="true">⚫</span>
                              ) : (
                                <span className={s.dot} />
                              )}
                              {overallLabel}
                            </span>
                            <div className={s.registryMiniStatuses}>
                              <span title={statusLabel(vehicle.status)}>
                                <i className={`${s.miniStatusDot} ${statusClass(vehicle.status)}`} />
                                {registryText("fleet_status_inspection_short")}
                              </span>
                              <span title={insuranceStatusText}>
                                <i className={`${s.miniStatusDot} ${statusClass(vehicle.insurance_state)}`} />
                                {registryText("fleet_status_insurance_short")}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td data-label={registryText("fleet_column_vehicle")}>
                          <div className={s.registryIdentityCell}>
                            <div className={s.registryPrimaryLine}>
                              <strong>{vehicle.registration_number || "—"}</strong>
                              {vehicle.registration_number && (
                                <button
                                  type="button"
                                  className={s.copyButtonV2}
                                  onClick={() => copyIdentifier(vehicle.registration_number, plateCopyKey)}
                                  title={registryText("fleet_copy_registration")}
                                  aria-label={registryText("fleet_copy_registration")}
                                >
                                  {copiedKey === plateCopyKey ? "✓" : "⧉"}
                                </button>
                              )}
                            </div>
                            <div className={s.registrySecondaryLine} title={vehicleDescription}>
                              {vehicleDescription || "—"}
                            </div>
                          </div>
                        </td>
                        <td data-label={registryText("fleet_column_vin")}>
                          <div className={s.registryIdentityCell}>
                            <div className={`${s.registryPrimaryLine} ${s.mono}`}>
                              <span title={vehicle.vin || ""}>{vehicle.vin || "—"}</span>
                              {vehicle.vin && (
                                <button
                                  type="button"
                                  className={s.copyButtonV2}
                                  onClick={() => copyIdentifier(vehicle.vin, vinCopyKey)}
                                  title={registryText("fleet_copy_vin")}
                                  aria-label={registryText("fleet_copy_vin")}
                                >
                                  {copiedKey === vinCopyKey ? "✓" : "⧉"}
                                </button>
                              )}
                            </div>
                          </div>
                        </td>
                        <td data-label={registryText("fleet_column_inspection")}>
                          <div className={s.registryDataCell}>
                            <strong>{formatDate(vehicle.inspection_valid_until)}</strong>
                            <span>{inspectionDaysText}</span>
                          </div>
                        </td>
                        <td data-label={registryText("fleet_column_insurance")}>
                          <div className={s.registryDataCell}>
                            <strong title={vehicle.insurance_provider || ""}>
                              {vehicle.insurance_provider || t("fleet_insurance_not_set")}
                            </strong>
                            <div className={s.registryInsuranceMeta}>
                              {vehicle.insurance_document_number && (
                                <span>{registryText("fleet_insurance_policy_short", { number: vehicle.insurance_document_number })}</span>
                              )}
                              <span className={`${s.insuranceStateText} ${statusClass(vehicle.insurance_state)}`}>
                                {insuranceStatusText}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className={s.registryActionsTd} data-label={registryText("fleet_column_actions")}>
                          <div className={s.registryActionsV2}>
                            <button
                              type="button"
                              className={s.registryActionSecondary}
                              onClick={() => openRegistryCheck(vehicle)}
                            >
                              ↗ {registryText("fleet_registry_check")}
                            </button>
                            <button
                              type="button"
                              className={s.registryActionPrimary}
                              onClick={() => startInspectionUpdate(vehicle)}
                            >
                              ✎ {registryText("fleet_change_data_short")}
                            </button>
                            <button
                              type="button"
                              className={s.registryActionSecondary}
                              onClick={() => history(vehicle)}
                            >
                              ☷ {registryText("fleet_history_short")}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {!paginatedVehicles.length && (
                <div className={s.filteredEmpty}>{registryText("fleet_filter_no_results")}</div>
              )}
            </div>

            <div className={s.paginationBarV2}>
              <div className={s.paginationInfoV2}>
                {registryText("fleet_pagination_range", {
                  from: filteredVehicles.length ? (currentPage - 1) * pageSize + 1 : 0,
                  to: Math.min(currentPage * pageSize, filteredVehicles.length),
                  total: filteredVehicles.length,
                })}
              </div>
              <div className={s.paginationControlsV2}>
                <label className={s.pageSizeControlV2}>
                  <span>{registryText("fleet_rows_per_page")}</span>
                  <select
                    value={pageSize}
                    onChange={(event) => {
                      setPageSize(Number(event.target.value));
                      setPage(1);
                    }}
                  >
                    {[10, 20, 50, 100].map((size) => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  className={s.pageNavButtonV2}
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                  disabled={currentPage <= 1}
                  title={registryText("fleet_previous_page")}
                >
                  ‹
                </button>
                <div className={s.pageNumbersV2}>
                  {paginationItems.map((item) =>
                    typeof item === "number" ? (
                      <button
                        type="button"
                        key={item}
                        className={`${s.pageNumberButtonV2} ${item === currentPage ? s.pageNumberActiveV2 : ""}`}
                        onClick={() => setPage(item)}
                      >
                        {item}
                      </button>
                    ) : (
                      <span key={item} className={s.pageEllipsisV2}>…</span>
                    )
                  )}
                </div>
                <button
                  type="button"
                  className={s.pageNavButtonV2}
                  onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                  disabled={currentPage >= totalPages}
                  title={registryText("fleet_next_page")}
                >
                  ›
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className={s.empty}>
            <div className={s.emptyIcon}>🚚</div>
            <div className={s.emptyTitle}>{t("fleet_empty_title")}</div>
            <div className={s.emptyText}>{t("fleet_empty_text")}</div>
            <button
              className={s.btn}
              style={{ marginTop: 14 }}
              onClick={() => { setModalError(""); setShowAdd(true); }}
            >
              {t("fleet_add_first")}
            </button>
          </div>
        )}
      </div>

      {showAdd && (
        <div
          className={s.modalBackdrop}
          onMouseDown={(event) =>
            event.target === event.currentTarget && closeAdd()
          }
        >
          <div className={`${s.modal} ${s.vehicleLookupModal}`}>
            <div className={s.modalHead}>
              <div>
                <div className={s.eyebrow}>{t("fleet_free_registry")}</div>
                <h3 className={s.modalTitle}>{t("fleet_add_vehicle")}</h3>
                <div className={s.panelSub}>{t("fleet_manual_add_subtitle")}</div>
              </div>
              <button
                type="button"
                className={s.modalClose}
                onClick={closeAdd}
                aria-label={t("close")}
              >
                ✕
              </button>
            </div>

            <form onSubmit={addManual} className={s.modalForm}>
              <div className={s.modalBody}>
                {modalError && <div className={s.modalAlert}>{modalError}</div>}

                <BatchUploadBox />

                {batchItems.length === 0 && (<>
                <div className={s.manualDivider}>
                  <span>{modalText("fleet_or_add_manually")}</span>
                </div>

                <div className={s.manualCompactGrid}>
                <section className={`${s.cleanSection} ${s.manualCompactSection}`}>
                  <div className={s.cleanSectionHead}>
                    <div>
                      <h4>{t("fleet_identity_title")}</h4>
                      <p>{modalText("fleet_identity_hint_compact")}</p>
                    </div>
                  </div>

                  <div className={s.equalGrid}>
                    <div className={s.cleanField}>
                      <label>{t("fleet_registration")} *</label>
                      <input
                        className={`${s.input} ${s.uniformInput}`}
                        value={registrationNumber}
                        onChange={(event) =>
                          setRegistrationNumber(event.target.value.toUpperCase())
                        }
                        placeholder={t("fleet_registration_placeholder")}
                        maxLength={16}
                        autoComplete="off"
                      />
                      <small>{t("fleet_registration_hint")}</small>
                      {registrationNumber && !registrationValid && (
                        <div className={s.fieldError}>{t("fleet_registration_validation")}</div>
                      )}
                    </div>

                    <div className={s.cleanField}>
                      <label>{t("fleet_vin")}</label>
                      <input
                        className={`${s.input} ${s.uniformInput} ${s.monoInput}`}
                        value={vin}
                        onChange={(event) => setVin(event.target.value.toUpperCase())}
                        placeholder={t("fleet_vin_placeholder")}
                        maxLength={17}
                        autoComplete="off"
                      />
                      <small>{t("fleet_vin_hint")}</small>
                      {vin && !vinValid && (
                        <div className={s.fieldError}>{t("fleet_vin_validation")}</div>
                      )}
                    </div>
                  </div>
                </section>


                <section className={`${s.cleanSection} ${s.manualCompactSection}`}>
                  <div className={s.cleanSectionHead}>
                    <div>
                      <h4>{t("fleet_inspection_details_title")}</h4>
                      <p>{t("fleet_inspection_details_hint")}</p>
                    </div>
                  </div>

                  <div className={s.equalGrid}>
                    <div className={s.cleanField}>
                      <label>{t("fleet_inspection_date_optional")}</label>
                      <input
                        type="date"
                        className={`${s.input} ${s.uniformInput}`}
                        value={inspectionDate}
                        onChange={(event) => setInspectionDate(event.target.value)}
                      />
                      <small>{t("fleet_inspection_date_optional_hint")}</small>
                    </div>
                    <div className={s.cleanField}>
                      <label>{t("fleet_valid_until")} *</label>
                      <input
                        type="date"
                        className={`${s.input} ${s.uniformInput}`}
                        value={validUntil}
                        onChange={(event) => setValidUntil(event.target.value)}
                        required
                      />
                      <small>{t("fleet_valid_until_hint")}</small>
                    </div>
                  </div>
                </section>
                </div>

                <section className={`${s.cleanSection} ${s.insuranceAccordion}`}>
                  <button
                    type="button"
                    className={s.accordionHeader}
                    onClick={() => setInsuranceExpanded((value) => !value)}
                    aria-expanded={insuranceExpanded}
                  >
                    <div className={s.accordionTitle}>
                      <span className={s.accordionIcon}>◇</span>
                      <div>
                        <strong>{t("fleet_insurance_title")}</strong>
                        <small>{t("fleet_insurance_hint")}</small>
                      </div>
                    </div>
                    <div className={s.accordionMeta}>
                      <span className={s.optionalBadge}>
                        {insuranceProvider || insuranceDocumentNumber || insuranceValidUntil
                          ? t("fleet_data_present")
                          : t("optional")}
                      </span>
                      <span className={s.accordionChevron}>{insuranceExpanded ? "⌃" : "⌄"}</span>
                    </div>
                  </button>

                  {insuranceExpanded && (
                    <div className={s.accordionBody}>
                      <div className={s.equalGrid}>
                        <div className={s.cleanField}>
                          <label>{t("fleet_insurance_provider")}</label>
                          <input className={`${s.input} ${s.uniformInput}`} value={insuranceProvider} onChange={(e) => setInsuranceProvider(e.target.value)} placeholder={t("fleet_insurance_provider_placeholder")} />
                        </div>
                        <div className={s.cleanField}>
                          <label>{t("fleet_insurance_document")}</label>
                          <input className={`${s.input} ${s.uniformInput} ${s.monoInput}`} value={insuranceDocumentNumber} onChange={(e) => setInsuranceDocumentNumber(e.target.value.toUpperCase())} placeholder={t("fleet_insurance_document_placeholder")} />
                        </div>
                        <div className={s.cleanField}>
                          <label>{t("fleet_insurance_type")}</label>
                          <input className={`${s.input} ${s.uniformInput}`} value={insuranceType} onChange={(e) => setInsuranceType(e.target.value)} placeholder={t("fleet_insurance_type_placeholder")} />
                        </div>
                        <div className={s.cleanField}>
                          <label>{t("fleet_insurance_status")}</label>
                          <select className={`${s.input} ${s.uniformInput} ${s.uniformSelect}`} value={insuranceStatus} onChange={(e) => setInsuranceStatus(e.target.value)}>
                            <option value="unknown">{t("fleet_insurance_status_unknown")}</option>
                            <option value="active">{t("fleet_insurance_status_active")}</option>
                            <option value="future">{t("fleet_insurance_status_future")}</option>
                            <option value="expired">{t("fleet_insurance_status_expired")}</option>
                          </select>
                        </div>
                        <div className={s.cleanField}>
                          <label>{t("fleet_insurance_valid_from")}</label>
                          <input type="date" className={`${s.input} ${s.uniformInput}`} value={insuranceValidFrom} onChange={(e) => setInsuranceValidFrom(e.target.value)} />
                        </div>
                        <div className={s.cleanField}>
                          <label>{t("fleet_insurance_valid_until")}</label>
                          <input type="date" className={`${s.input} ${s.uniformInput}`} value={insuranceValidUntil} onChange={(e) => setInsuranceValidUntil(e.target.value)} />
                          <small>{t("fleet_insurance_valid_until_hint")}</small>
                        </div>
                      </div>
                    </div>
                  )}
                </section>

                <div className={s.inlinePrivacy}>
                  <span>✓</span>
                  <div>
                    <strong>{t("fleet_private_ocr_title")}</strong>
                    <small>{t("fleet_private_ocr_text")}</small>
                  </div>
                </div>
                </>)}

              </div>

              <div className={s.modalFooter}>
                <div className={s.modalFooterNote}>{t("fleet_manual_footer")}</div>
                <div className={s.formActions}>
                  <button type="button" className={s.btnSecondary} onClick={closeAdd}>
                    {batchItems.length ? t("close") : t("cancel")}
                  </button>
                  {batchItems.length === 0 && (
                    <button
                      type="submit"
                      className={s.btn}
                      disabled={!canSaveVehicle || saving || ocrLoading}
                    >
                      {saving ? t("saving") : modalText("fleet_manual_save_button")}
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {registryCheckVehicle && (
        <div className={s.modalBackdrop} onMouseDown={(event) => {
          if (event.target === event.currentTarget) setRegistryCheckVehicle(null);
        }}>
          <div className={`${s.modal} ${s.registryCheckModal}`} role="dialog" aria-modal="true">
            <div className={s.modalHead}>
              <div>
                <div className={s.eyebrow}>{registryText("fleet_check_modal_eyebrow")}</div>
                <h3 className={s.modalTitle}>{registryText("fleet_check_modal_title")}</h3>
                <div className={s.panelSub}>{registryText("fleet_check_modal_hint")}</div>
              </div>
              <button
                type="button"
                className={s.modalClose}
                onClick={() => setRegistryCheckVehicle(null)}
                aria-label={registryText("fleet_check_close")}
              >
                ✕
              </button>
            </div>

            <div className={s.registryCheckBody}>
              <div className={s.registryCheckVehicleSummary}>
                <strong>{registryCheckVehicle.registration_number || "—"}</strong>
                <span>{[registryCheckVehicle.make, registryCheckVehicle.model, registryCheckVehicle.year].filter(Boolean).join(" · ") || "—"}</span>
              </div>

              <div className={s.registryCheckFields}>
                <label className={s.registryCheckField}>
                  <span>{registryText("fleet_check_registration")}</span>
                  <div className={s.registryCheckInputGroup}>
                    <input readOnly value={registryCheckVehicle.registration_number || ""} />
                    <button
                      type="button"
                      onClick={() => copyIdentifier(registryCheckVehicle.registration_number, `check-plate-${registryCheckVehicle.id}`)}
                      disabled={!registryCheckVehicle.registration_number}
                    >
                      {copiedKey === `check-plate-${registryCheckVehicle.id}` ? `✓ ${registryText("fleet_check_copied")}` : `⧉ ${registryText("fleet_check_copy")}`}
                    </button>
                  </div>
                </label>

                <label className={s.registryCheckField}>
                  <span>{registryText("fleet_check_vin")}</span>
                  <div className={s.registryCheckInputGroup}>
                    <input className={s.mono} readOnly value={registryCheckVehicle.vin || ""} />
                    <button
                      type="button"
                      onClick={() => copyIdentifier(registryCheckVehicle.vin, `check-vin-${registryCheckVehicle.id}`)}
                      disabled={!registryCheckVehicle.vin}
                    >
                      {copiedKey === `check-vin-${registryCheckVehicle.id}` ? `✓ ${registryText("fleet_check_copied")}` : `⧉ ${registryText("fleet_check_copy")}`}
                    </button>
                  </div>
                </label>
              </div>

              <div className={s.registryCheckNote}>
                <span>ⓘ</span>
                <p>{registryText("fleet_check_external_note")}</p>
              </div>
            </div>

            <div className={s.modalFooter}>
              <div className={s.modalFooterNote}>
                {registryCheckVehicle.inspection_valid_until
                  ? `${registryText("fleet_column_inspection")}: ${formatDate(registryCheckVehicle.inspection_valid_until)}`
                  : registryText("fleet_column_inspection")}
              </div>
              <div className={s.formActions}>
                <button type="button" className={s.btnSecondary} onClick={() => setRegistryCheckVehicle(null)}>
                  {registryText("fleet_check_close")}
                </button>
                <button type="button" className={s.btn} onClick={copyAndOpenRegistry}>
                  ↗ {registryCheckVehicle.registration_number
                    ? registryText("fleet_check_copy_open")
                    : registryText("fleet_check_open")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {editInspection && (
        <div
          className={s.modalBackdrop}
          onMouseDown={(event) =>
            event.target === event.currentTarget && closeInspection()
          }
        >
          <div className={`${s.modal} ${s.vehicleLookupModal} ${s.editVehicleModal}`}>
            <div className={s.modalHead}>
              <div>
                <div className={s.eyebrow}>{editInspection.registration_number}</div>
                <h3 className={s.modalTitle}>{t("fleet_change_data")}</h3>
                <div className={s.panelSub}>{t("fleet_update_inspection_subtitle")}</div>
              </div>
              <button type="button" className={s.modalClose} onClick={closeInspection} aria-label={t("close")}>✕</button>
            </div>

            <form onSubmit={saveInspection} className={s.modalForm}>
              <div className={s.modalBody}>
                {modalError && <div className={s.modalAlert}>{modalError}</div>}

                <div className={s.vehicleIdentitySummary}>
                  <div>
                    <span>{t("fleet_registration")}</span>
                    <b>{editInspection.registration_number}</b>
                  </div>
                  <div>
                    <span>{t("fleet_vin")}</span>
                    <b className={s.mono}>{editInspection.vin || "—"}</b>
                  </div>
                </div>

                <section className={s.cleanSection}>
                  <div className={s.sourceRow}>
                    <div>
                      <h4>{t("fleet_open_registry")}</h4>
                      <p>{t("fleet_open_registry_hint")}</p>
                    </div>
                    <button type="button" className={s.registryCompact} onClick={openRegistry}>
                      <span>↗</span>
                      {t("fleet_open_registry")}
                    </button>
                  </div>
                  <OcrBox mode="update" />
                </section>

                <section className={s.cleanSection}>
                  <div className={s.cleanSectionHead}>
                    <div>
                      <h4>{t("fleet_inspection_details_title")}</h4>
                      <p>{t("fleet_inspection_details_hint")}</p>
                    </div>
                  </div>
                  <div className={s.equalGrid}>
                    <div className={s.cleanField}>
                      <label>{t("fleet_inspection_date_optional")}</label>
                      <input type="date" className={`${s.input} ${s.uniformInput}`} value={newInspectionDate} onChange={(event) => setNewInspectionDate(event.target.value)} />
                    </div>
                    <div className={s.cleanField}>
                      <label>{t("fleet_valid_until")} *</label>
                      <input type="date" className={`${s.input} ${s.uniformInput}`} value={newValidUntil} onChange={(event) => setNewValidUntil(event.target.value)} required />
                    </div>
                  </div>
                </section>

                <section className={`${s.cleanSection} ${s.insuranceAccordion}`}>
                  <button type="button" className={s.accordionHeader} onClick={() => setInsuranceExpanded((value) => !value)} aria-expanded={insuranceExpanded}>
                    <div className={s.accordionTitle}>
                      <span className={s.accordionIcon}>◇</span>
                      <div>
                        <strong>{t("fleet_insurance_title")}</strong>
                        <small>{t("fleet_insurance_hint")}</small>
                      </div>
                    </div>
                    <div className={s.accordionMeta}>
                      <span className={s.optionalBadge}>
                        {newInsuranceProvider || newInsuranceDocumentNumber || newInsuranceValidUntil
                          ? t("fleet_data_present")
                          : t("optional")}
                      </span>
                      <span className={s.accordionChevron}>{insuranceExpanded ? "⌃" : "⌄"}</span>
                    </div>
                  </button>
                  {insuranceExpanded && (
                    <div className={s.accordionBody}>
                      <div className={s.equalGrid}>
                        <div className={s.cleanField}>
                          <label>{t("fleet_insurance_provider")}</label>
                          <input className={`${s.input} ${s.uniformInput}`} value={newInsuranceProvider} onChange={(e) => setNewInsuranceProvider(e.target.value)} />
                        </div>
                        <div className={s.cleanField}>
                          <label>{t("fleet_insurance_document")}</label>
                          <input className={`${s.input} ${s.uniformInput} ${s.monoInput}`} value={newInsuranceDocumentNumber} onChange={(e) => setNewInsuranceDocumentNumber(e.target.value.toUpperCase())} />
                        </div>
                        <div className={s.cleanField}>
                          <label>{t("fleet_insurance_type")}</label>
                          <input className={`${s.input} ${s.uniformInput}`} value={newInsuranceType} onChange={(e) => setNewInsuranceType(e.target.value)} />
                        </div>
                        <div className={s.cleanField}>
                          <label>{t("fleet_insurance_status")}</label>
                          <select className={`${s.input} ${s.uniformInput} ${s.uniformSelect}`} value={newInsuranceStatus} onChange={(e) => setNewInsuranceStatus(e.target.value)}>
                            <option value="unknown">{t("fleet_insurance_status_unknown")}</option>
                            <option value="active">{t("fleet_insurance_status_active")}</option>
                            <option value="future">{t("fleet_insurance_status_future")}</option>
                            <option value="expired">{t("fleet_insurance_status_expired")}</option>
                          </select>
                        </div>
                        <div className={s.cleanField}>
                          <label>{t("fleet_insurance_valid_from")}</label>
                          <input type="date" className={`${s.input} ${s.uniformInput}`} value={newInsuranceValidFrom} onChange={(e) => setNewInsuranceValidFrom(e.target.value)} />
                        </div>
                        <div className={s.cleanField}>
                          <label>{t("fleet_insurance_valid_until")}</label>
                          <input type="date" className={`${s.input} ${s.uniformInput}`} value={newInsuranceValidUntil} onChange={(e) => setNewInsuranceValidUntil(e.target.value)} />
                        </div>
                      </div>
                    </div>
                  )}
                </section>

                <div className={s.resetNotice}>🔔 <span>{t("fleet_update_resets_reminders")}</span></div>
              </div>

              <div className={s.modalFooter}>
                <div className={s.modalFooterNote}>{t("fleet_new_cycle_note")}</div>
                <div className={s.formActions}>
                  <button type="button" className={s.btnSecondary} onClick={closeInspection}>{t("cancel")}</button>
                  <button className={s.btn} disabled={!newValidUntil || saving || ocrLoading}>
                    {saving ? t("saving") : t("fleet_apply_new_date")}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {drawer && (
        <>
          <div className={s.drawerBackdrop} onClick={() => setDrawer(null)} />
          <aside className={s.drawer}>
            <div className={s.drawerHead}>
              <div>
                <div className={s.eyebrow}>{t("timeline")}</div>
                <h3 className={s.panelTitle}>{drawer.registration_number}</h3>
              </div>
              <button className={s.btnGhost} onClick={() => setDrawer(null)}>
                ✕
              </button>
            </div>
            <div className={s.drawerBody}>
              {timeline.length ? (
                <div className={s.timeline}>
                  {timeline.map((event) => (
                    <div className={s.event} key={event.id}>
                      <div className={s.eventTitle}>
                        {timelineText("action", event)}
                      </div>
                      <div className={s.eventDesc}>
                        {timelineText("desc", event)}
                      </div>
                      {event.actor && (
                        <div className={s.eventActor}>
                          👤 {t("fleet_history_actor", { defaultValue: "Изменил: {{name}}", name: event.actor.full_name || event.actor.email || `#${event.actor.ui_id || event.actor.id}` })}
                          {event.acted_on_behalf && event.target ? ` → ${event.target.full_name || event.target.email || `#${event.target.ui_id || event.target.id}`}` : ""}
                        </div>
                      )}
                      <div className={s.eventTime}>
                        {new Date(event.created_at).toLocaleString(i18n.language)}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={s.empty}>{t("timeline_empty")}</div>
              )}
            </div>
          </aside>
        </>
      )}
    </div>
  );
}
